import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { displayOrder } from '../utils/resultOrder';
import { siteRecordLinks } from '../utils/sourceLinks';
import { useAuth0 } from '@auth0/auth0-react';
import '../styles/SiteFinder.css';
import { calcAreaRequirement, calcVoltageFit, calcWaterNeeds, requiredVoltage, suggestedGridDistanceKm, getPriceZone, calcAmmoniaPlantArea, calcAmmoniaWater, siteZone } from '../utils/projectCalcs';
import PINNED_SITES_DATA from '../data/pinned_sites.json';
import { useTranslation } from '../translations';
import { useProjects } from '../contexts/ProjectsContext';
import { useIsPremium } from '../contexts/PremiumContext';
import { useRegionLock } from '../contexts/RegionLockContext';
import { useOrg } from '../contexts/OrgContext';
import { tenantUseCase, tenantCountry } from '../utils/tenantScope';
import { FREE_TIER_BBOX, NO_PREMIUM_DEFAULT_BBOX, intersectBbox } from '../utils/helgeland';
import { CALENDLY_URL, calendlyWithPrefill, openCalendly } from '../utils/calendly';
import CoordinateSearchBox from './CoordinateSearchBox';
import ConfirmedCapacityResults, { isSufficientSubstation } from './ConfirmedCapacityResults';
import { planCodeCategory, isIndustryGrade } from '../utils/planCodes';
import { nearestHomeText } from '../utils/format';

// All Premium CTAs route to Calendly with the user's email pre-filled
// (Implementation Brief 2026-06-07). Use openCalendly(user) for click
// handlers; UPGRADE_URL kept for any anchor-tag fallback (e.g. when
// auth context isn't reachable).
const UPGRADE_URL = CALENDLY_URL;
const FREE_TIER_PROJECT_CAP = 3;
const FREE_TIER_RESULT_OPTIONS = new Set([10, 25]);
const FREE_TIER_DEFAULT_RESULT = 25;

// Finnish municipalities (kunnat) — major sites relevant for H2/DC siting
const FI_KUNNAT = [
  { code: '091', name: 'Helsinki',    region: 'Uusimaa' },
  // Codes verified 2026-04-09 against Tilastokeskus official register
  // (kunta4500k_2026 layer at https://geo.stat.fi/geoserver/tilastointialueet/wfs).
  // Earlier values were partly from a non-canonical source — 13 of 29 entries
  // were wrong, causing e.g. a "Rovaniemi" pick to silently search Pori.
  { code: '049', name: 'Espoo',       region: 'Uusimaa' },
  { code: '837', name: 'Tampere',     region: 'Pirkanmaa' },
  { code: '564', name: 'Oulu',        region: 'Pohjois-Pohjanmaa' },
  { code: '853', name: 'Turku',       region: 'Varsinais-Suomi' },
  { code: '245', name: 'Kerava',      region: 'Uusimaa' },
  { code: '179', name: 'Jyväskylä',   region: 'Keski-Suomi' },
  { code: '398', name: 'Lahti',       region: 'Päijät-Häme' },
  { code: '286', name: 'Kouvola',     region: 'Kymenlaakso' },
  { code: '609', name: 'Pori',        region: 'Satakunta' },
  { code: '405', name: 'Lappeenranta',region: 'Etelä-Karjala' },
  { code: '698', name: 'Rovaniemi',   region: 'Lappi' },
  { code: '743', name: 'Seinäjoki',   region: 'Etelä-Pohjanmaa' },
  { code: '272', name: 'Kokkola',     region: 'Keski-Pohjanmaa' },
  { code: '140', name: 'Iisalmi',     region: 'Pohjois-Savo' },
  { code: '491', name: 'Mikkeli',     region: 'Etelä-Savo' },
  { code: '167', name: 'Joensuu',     region: 'Pohjois-Karjala' },
  { code: '905', name: 'Vaasa',       region: 'Pohjanmaa' },
  { code: '092', name: 'Vantaa',      region: 'Uusimaa' },
  { code: '205', name: 'Kajaani',     region: 'Kainuu' },
  { code: '240', name: 'Kemi',        region: 'Lappi' },
  { code: '241', name: 'Keminmaa',    region: 'Lappi' },
  { code: '851', name: 'Tornio',      region: 'Lappi' },
  { code: '244', name: 'Kempele',     region: 'Pohjois-Pohjanmaa' },
  { code: '425', name: 'Liminka',     region: 'Pohjois-Pohjanmaa' },
  { code: '494', name: 'Muhos',       region: 'Pohjois-Pohjanmaa' },
  { code: '139', name: 'Ii',          region: 'Pohjois-Pohjanmaa' },
  { code: '615', name: 'Pudasjärvi',  region: 'Pohjois-Pohjanmaa' },
  { code: '625', name: 'Pyhäjoki',    region: 'Pohjois-Pohjanmaa' },
  { code: '684', name: 'Rauma',       region: 'Satakunta' },
  { code: '734', name: 'Salo',        region: 'Varsinais-Suomi' },
  { code: '075', name: 'Hamina',      region: 'Kymenlaakso' },
  { code: '285', name: 'Kotka',       region: 'Kymenlaakso' },
  { code: '678', name: 'Raahe',       region: 'Pohjois-Pohjanmaa' },
  { code: '287', name: 'Kristiinankaupunki', region: 'Pohjanmaa' },
  { code: '257', name: 'Kirkkonummi', region: 'Uusimaa' },
  { code: '410', name: 'Laukaa',      region: 'Keski-Suomi' },
];
const FI_KUNTA_NAMES = Object.fromEntries(FI_KUNNAT.map(m => [m.code, m.name]));

// FI_KUNTA_BOUNDS removed 2026-04-09. The hand-typed table was keyed by
// the wrong kunta numbers (matched the buggy FI_KUNNAT) and only covered
// 18 of the 29 cities in the picker — silently dropping the bbox for the
// rest. Replaced by sending `municipality_numbers` to the backend, which
// resolves the real bounds via the Tilastokeskus WFS (with TM35FIN →
// WGS84 conversion). Single source of truth, no hand-typed coords.

// ZONE_DEMO_OVERRIDES removed 2026-06-05. The override silently
// relabeled DIBK "Fritid og turistformål" (recreation/tourism, zone
// code 1300) as "Næringseiendom" (industrial property) on the
// result card — but the actual screening pipeline still classified
// the parcel as rezoning (DIBK says no industrial). Users opening
// the parcel saw "industrial" in the list, "Fritid og turistformål"
// in the Site Analysis panel, and the parcel sorted under
// "rezoning candidates" — three contradictory signals. Showing the
// real zone name everywhere is the honest fix.
function overrideZone(code, name) {
  return { code, name };
}

function ZoneBadge({ code, name }) {
  // Colour by the official plan-code reading (utils/planCodes): green for
  // land zoned for industry, energy or harbour use; amber for other
  // facilities and extraction; orange for LNFR and sea; grey otherwise.
  const cat = planCodeCategory(code);
  const isIndustry = isIndustryGrade(code);
  const isRaw = !isIndustry && (cat === 'facility' || cat === 'extraction');
  const isLNFR = cat === 'lnfr' || cat === 'sea';
  const bg = isIndustry ? '#16a34a' : isRaw ? '#ca8a04' : isLNFR ? '#ea580c' : '#7c829e';
  const label = code ? (name || code) : 'Unknown ⚠';
  return <span className="zone-badge" style={{ background: bg }}>{label}</span>;
}

function getGridCapexEstimate(site) {
  const dist = site.grid_distance_km || 2;
  return Math.round(4 * dist);  // 4 MNOK/km all-in, no separate transformer
}

// Outdated (checked 2026-10-14 against Kartverket's list: 65 codes carry
// the wrong name, e.g. 3905 is Tønsberg, not Bamble). Only the unreachable
// find-zones results still read it; the municipality picker takes names
// from /sites/zone-municipalities (backend/app/data/kommuner_no.json).
const KOMMUNE_NAMES = {
  "0301":"Oslo",
  "1101":"Eigersund","1103":"Stavanger","1106":"Haugesund","1108":"Sandnes","1111":"Sokndal","1112":"Lund","1114":"Bjerkreim","1119":"Hå","1120":"Klepp","1121":"Time","1122":"Gjesdal","1124":"Sola","1127":"Randaberg","1130":"Strand","1133":"Hjelmeland","1134":"Suldal","1135":"Sauda","1144":"Kvitsøy","1145":"Bokn","1146":"Tysvær","1149":"Karmøy","1151":"Utsira","1160":"Vindafjord",
  "1505":"Kristiansund","1506":"Molde","1507":"Ålesund","1511":"Vanylven","1514":"Sande","1515":"Herøy","1516":"Ulstein","1517":"Hareid","1519":"Volda","1520":"Ørsta","1525":"Stranda","1528":"Sykkylven","1531":"Sula","1532":"Giske","1535":"Vestnes","1539":"Rauma","1547":"Aukra","1554":"Averøy","1557":"Gjemnes","1560":"Tingvoll","1563":"Sunndal","1566":"Surnadal","1573":"Smøla","1576":"Aure","1577":"Hustadvika",
  "1804":"Bodø","1811":"Bindal","1812":"Sømna","1813":"Brønnøy","1815":"Vega","1816":"Vevelstad","1818":"Herøy","1820":"Alstahaug","1822":"Leirfjord","1824":"Vefsn","1825":"Grane","1826":"Hattfjelldal","1827":"Dønna","1828":"Nesna","1832":"Hemnes","1833":"Rana","1834":"Lurøy","1835":"Træna","1836":"Rødøy","1837":"Meløy","1838":"Gildeskål","1839":"Beiarn","1840":"Saltdal","1841":"Fauske","1845":"Sørfold","1848":"Steigen","1851":"Lødingen","1853":"Evenes","1856":"Røst","1857":"Værøy","1859":"Flakstad","1860":"Vestvågøy","1865":"Vågan","1866":"Hadsel","1867":"Bø","1868":"Øksnes","1870":"Sortland","1871":"Andøy","1874":"Moskenes","1875":"Hamarøy",
  "3101":"Halden","3103":"Moss","3105":"Sarpsborg","3107":"Fredrikstad","3110":"Hvaler","3112":"Råde","3114":"Våler","3116":"Skiptvet","3118":"Indre Østfold","3120":"Rakkestad","3122":"Marker","3124":"Aremark",
  "3201":"Bærum","3203":"Asker","3205":"Lillestrøm","3207":"Nordre Follo","3209":"Ullensaker","3212":"Nesodden","3214":"Frogn","3216":"Vestby","3218":"Ås","3220":"Enebakk","3222":"Lørenskog","3224":"Rælingen","3226":"Aurskog-Høland","3228":"Nes","3230":"Gjerdrum","3232":"Nittedal","3234":"Lunner","3236":"Jevnaker","3238":"Nannestad","3240":"Eidsvoll","3242":"Hurdal",
  "3301":"Drammen","3303":"Kongsberg","3305":"Ringerike","3310":"Numedal","3311":"Flesberg","3312":"Rollag","3313":"Nore og Uvdal","3314":"Sigdal","3315":"Krødsherad","3316":"Modum","3317":"Øvre Eiker","3318":"Lier","3319":"Røyken","3320":"Hurum","3321":"Flå","3322":"Nes","3323":"Gol","3324":"Hemsedal","3325":"Ål","3326":"Hol",
  "3401":"Kongsvinger","3403":"Hamar","3405":"Lillehammer","3407":"Gjøvik","3411":"Ringsaker","3412":"Løten","3413":"Stange","3414":"Nord-Odal","3415":"Sør-Odal","3416":"Eidskog","3417":"Grue","3418":"Åsnes","3419":"Våler","3420":"Elverum","3421":"Trysil","3422":"Åmot","3423":"Stor-Elvdal","3424":"Rendalen","3425":"Engerdal","3426":"Tolga","3427":"Tynset","3428":"Alvdal","3429":"Folldal","3430":"Os","3431":"Dovre","3432":"Lesja","3433":"Skjåk","3434":"Lom","3435":"Vågå","3436":"Nord-Fron","3437":"Sel","3438":"Sør-Fron","3439":"Ringebu","3440":"Øyer","3441":"Gausdal","3442":"Østre Toten","3443":"Vestre Toten","3446":"Gran","3447":"Søndre Land","3448":"Nordre Land","3449":"Sør-Aurdal","3450":"Etnedal","3451":"Nord-Aurdal","3452":"Vestre Slidre","3453":"Øystre Slidre",
  "3801":"Horten","3802":"Holmestrand","3803":"Tønsberg","3804":"Sandefjord","3805":"Larvik","3806":"Færder",
  "3901":"Porsgrunn","3902":"Skien","3903":"Notodden","3904":"Siljan","3905":"Bamble","3906":"Kragerø","3907":"Drangedal","3908":"Nome","3909":"Midt-Telemark","3910":"Tinn","3911":"Hjartdal","3912":"Seljord","3913":"Kviteseid","3914":"Nissedal","3915":"Fyresdal","3916":"Tokke","3917":"Vinje",
  "4001":"Risør","4003":"Grimstad","4005":"Arendal","4010":"Gjerstad","4012":"Vegårshei","4014":"Tvedestrand","4016":"Froland","4018":"Lillesand","4020":"Birkenes","4022":"Åmli","4024":"Iveland","4026":"Evje og Hornnes","4028":"Bygland","4030":"Valle","4032":"Bykle","4034":"Vennesla","4036":"Åseral","4038":"Lyngdal","4040":"Hægebostad","4042":"Kvinesdal","4044":"Sirdal","4201":"Risør","4202":"Grimstad","4203":"Arendal","4204":"Kristiansand","4205":"Lindesnes","4206":"Farsund","4207":"Flekkefjord","4211":"Gjerstad","4212":"Vegårshei","4213":"Tvedestrand","4214":"Froland","4215":"Lillesand","4216":"Birkenes","4217":"Åmli","4218":"Iveland","4219":"Evje og Hornnes","4220":"Bygland","4221":"Valle","4222":"Bykle","4223":"Vennesla","4224":"Åseral","4225":"Lyngdal","4226":"Hægebostad","4227":"Kvinesdal","4228":"Sirdal",
  "4601":"Bergen","4602":"Kinn","4611":"Etne","4612":"Samnanger","4613":"Bjørnafjorden","4614":"Austevoll","4615":"Sund","4616":"Askøy","4617":"Kvam","4619":"Vaksdal","4620":"Modalen","4621":"Osterøy","4622":"Alver","4623":"Austrheim","4624":"Fedje","4625":"Masfjorden","4626":"Gulen","4627":"Solund","4628":"Hyllestad","4629":"Høyanger","4630":"Vik","4631":"Sogndal","4632":"Aurland","4633":"Lærdal","4634":"Årdal","4635":"Luster","4636":"Askvoll","4637":"Fjaler","4638":"Sunnfjord","4639":"Bremanger","4640":"Stad","4641":"Kinn","4642":"Stryn",
  "5001":"Trondheim","5006":"Steinkjer","5007":"Namsos","5014":"Frøya","5020":"Osen","5021":"Oppdal","5022":"Rennebu","5025":"Røros","5026":"Holtålen","5027":"Midtre Gauldal","5028":"Melhus","5029":"Skaun","5031":"Malvik","5032":"Selbu","5033":"Tydal","5034":"Meråker","5035":"Stjørdal","5036":"Frosta","5037":"Levanger","5038":"Verdal","5041":"Snåsa","5042":"Lierne","5043":"Røyrvik","5044":"Namsskogan","5045":"Grong","5046":"Høylandet","5047":"Overhalla","5049":"Flatanger","5052":"Leka","5053":"Inderøy","5054":"Indre Fosen","5055":"Heim","5056":"Hitra","5057":"Ørland","5058":"Åfjord","5059":"Orkland","5060":"Nærøysund","5061":"Rindal",
  "5401":"Tromsø","5402":"Harstad","5403":"Alta","5404":"Vardø","5405":"Vadsø","5406":"Hammerfest","5411":"Kvæfjord","5412":"Tjeldsund","5413":"Ibestad","5414":"Gratangen","5415":"Lavangen","5416":"Bardu","5417":"Salangen","5418":"Målselv","5419":"Sørreisa","5420":"Dyrøy","5421":"Senja","5422":"Balsfjord","5423":"Karlsøy","5424":"Lyngen","5425":"Storfjord","5426":"Kåfjord","5427":"Skjervøy","5428":"Nordreisa","5429":"Kvænangen","5430":"Kautokeino","5432":"Loppa","5433":"Hasvik","5434":"Måsøy","5435":"Nordkapp","5436":"Porsanger","5437":"Karasjok","5438":"Lebesby","5439":"Gamvik","5440":"Berlevåg","5441":"Tana","5442":"Nesseby","5443":"Båtsfjord","5444":"Sør-Varanger",
  "5501":"Tromsø","5503":"Harstad","5505":"Alta","5601":"Vardø","5603":"Vadsø","5605":"Sør-Varanger","4003":"Siljan",
};

async function checkDibkAtCoords(lng, lat) {
  const d = 0.01;
  const bbox = `${lat - d},${lng - d},${lat + d},${lng + d}`;
  const url = `https://nap.ft.dibk.no/services/wms/reguleringsplaner/?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetFeatureInfo` +
    `&LAYERS=arealformal_vn1&QUERY_LAYERS=arealformal_vn1&INFO_FORMAT=application/json` +
    `&I=128&J=128&WIDTH=256&HEIGHT=256&CRS=EPSG:4326&BBOX=${bbox}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 6000);
  try {
    const resp = await fetch(url, { signal: controller.signal });
    clearTimeout(timer);
    if (!resp.ok) return false;
    const json = await resp.json();
    return (json.features?.length ?? 0) > 0;
  } catch {
    clearTimeout(timer);
    return false;
  }
}

// Harbour + published grid capacity screen (2026-10-07). The three tests
// come from the siting brief; capacity is what each grid company publishes
// in Elbits WattApp (available / reserved / requested = queue).
export const SCREEN_TESTS = {
  t1: { label: 'Test 1', hint: '>5 MW free · 0 MW queue',     min_available_mw: 5,  max_queue_mw: 0 },
  t2: { label: 'Test 2', hint: '>5 MW free · <150 MW queue',  min_available_mw: 5,  max_queue_mw: 150 },
  t3: { label: 'Test 3', hint: '>20 MW free · any queue',     min_available_mw: 20, max_queue_mw: null },
};

// The region presets (?test_region= picker for premium users) were removed
// on 2026-10-14: a search covers all of Norway, the visible map or the
// chosen municipalities. ?test_region= now only names the free tier's
// region (RegionLockContext).

// Last-search persistence. Restores the results panel after a browser
// tab discard / reload / close so the user doesn't redo a 1–3 minute
// search. Bump the key suffix to invalidate older saved shapes if the
// saved schema changes.
//
// 2026-10-04: moved from sessionStorage (per-tab, 30 min) to
// localStorage (per-browser, 12 h) — users asked for results to survive
// closing the tab. Guarded by country + use-case so a NO/ammonia result
// set never resurrects over a FI or data-center session. The backend
// also caches identical searches in Redis (search_meta.served_from =
// "result_cache"), so re-running the same search is instant too.
const LAST_SEARCH_KEY = 'seidr_last_search_v2';
const LAST_SEARCH_TTL_MS = 12 * 60 * 60 * 1000;  // 12 h
const _lastSearchStore = () => {
  try { return window.localStorage; } catch { return null; }
};

const SiteFinder = ({ isOpen, onToggle, onClose, onSiteSelect, onSubstationPan, onSearchStart, mapBounds, parcelSelectMode, totalSelectedHa, onEnterParcelMode, onCriteriaChange, onResultsChange, onTeigSelect, onTeigResultsChange, onCountryChange, onUseCaseChange, onCoordLookup, language = 'en' }) => {
  const t = useTranslation(language);
  // Auth0 bearer attach for /sites/search. PR-0 auth-gated that endpoint
  // with Depends(verify_token); without the bearer, the search would
  // 401. SiteFinder's fetches go out raw (not through ProjectsContext's
  // authedFetch wrapper), so we read the token here and tack it onto
  // the Authorization header just before the fetch fires. Falls back
  // to anonymous if Auth0 isn't configured (DEV_AUTH_BYPASS dev path).
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { isPremium, loading: premiumLoading } = useIsPremium();
  // When locked (non-premium with a known test_region), hide premium-
  // gated UI: country picker, municipality search, hydrogen tab,
  // "All Norway" toggle, coord lookup beyond the region bbox. Backend
  // also clamps to the same bbox so a stripped URL doesn't widen the
  // search.
  const regionLock = useRegionLock();
  const { branding, loading: orgLoading } = useOrg();
  const { user: auth0User } = useAuth0();
  const { saveProject, isSaved, isSaving, projectCount, findSavedProject, deleteProject } = useProjects();
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const resultsRef = useRef(null);
  const progressRef = useRef(null);
  const [excludedSites, setExcludedSites] = useState([]);
  const [excludedExpanded, setExcludedExpanded] = useState(false);

  // Teig-first search (SSE) results + progress
  const [teigResults, setTeigResults] = useState(null);
  const [teigExcluded, setTeigExcluded] = useState([]);
  const [teigHardExcluded, setTeigHardExcluded] = useState([]);
  const [teigHardExcludedSummary, setTeigHardExcludedSummary] = useState({});
  const [teigFunnel, setTeigFunnel] = useState(null);
  // complete-event search_meta (served_from, cached_at, parcel_source) so
  // the results header can say when a run was replayed from the 24 h cache.
  const [searchMeta, setSearchMeta] = useState(null);
  // Options of the last run, so "Run live" repeats exactly that request.
  const lastSearchOptsRef = useRef({});
  const [teigExcludedExpanded, setTeigExcludedExpanded] = useState(false);
  const [progressLog, setProgressLog] = useState([]);
  const [wfsErrors, setWfsErrors] = useState([]);

  // Cursor tooltip for search status
  const [cursorTooltip, setCursorTooltip] = useState({ visible: false, text: '', fading: false });
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const cursorTooltipTimeoutRef = useRef(null);

  // Search mode: 'grid' = substation-first (existing), 'zone' = teig-first (SSE), 'smart' = grid-scan
  const [searchMode, setSearchMode] = useState('zone');
  const [zoneResults, setZoneResults] = useState(null);
  // Smart search (grid-scan) results
  const [smartResults, setSmartResults] = useState(null);
  const [smartSearching, setSmartSearching] = useState(false);

  // Feature 4: Substation picker
  const [subSearch, setSubSearch] = useState('');
  const [subResults, setSubResults] = useState([]);
  const [subLoading, setSubLoading] = useState(false);
  const [pinnedSubstations, setPinnedSubstations] = useState([]);
  const [subRadius, setSubRadius] = useState(5);
  const [subCandidates, setSubCandidates] = useState(null);
  const [subSearching, setSubSearching] = useState(false);
  const subDebounceRef = useRef(null);

  // DIBK zone coverage filter
  const [onlyZoneCoverage, setOnlyZoneCoverage] = useState(false);
  const [zoneChecks, setZoneChecks] = useState({});  // siteKey -> true/false/null
  const [zoneChecking, setZoneChecking] = useState(false);

  // Section collapse state
  const [showProjectParams, setShowProjectParams] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Free-tier defaults: snap the user onto the only choices that
  // aren't locked, so the UI never starts on a setting they then
  // can't change. Covers:
  //   • Result limit → 25 (50/100/200 are locked)
  // (The use case is the tenant's since 2026-10-14; the public site's is
  // Data Center, the free tier's only use case.)
  // (Voltage tier was on this list. Removed 2026-06-01 — voltage is
  // now derived from MW everywhere, so the free-tier 420 kV forcing
  // is gone. See minVoltage useMemo below.)
  useEffect(() => {
    if (premiumLoading || isPremium) return;
    setResultLimit((n) => (FREE_TIER_RESULT_OPTIONS.has(n) ? n : FREE_TIER_DEFAULT_RESULT));
  }, [premiumLoading, isPremium]);

  // Search scope
  const [searchAllNorway, setSearchAllNorway] = useState(true);

  // Municipality filter (zone-first mode)
  const [selectedMunicipalities, setSelectedMunicipalities] = useState([]);
  const [municipalityList, setMunicipalityList] = useState([]);
  const [muniSearch, setMuniSearch] = useState('');

  // Country and use case are the tenant's, not a choice in the panel
  // (2026-10-14): each organisation works in one country and one use case,
  // read from its branding (utils/tenantScope.js). The public site resolves
  // to data centres in Norway. The map is told whenever they resolve; the
  // branding arrives after the first render.
  const selectedCountry = useMemo(() => tenantCountry(branding), [branding]);
  const useCase = useMemo(() => tenantUseCase(branding), [branding]);
  useEffect(() => {
    if (onCountryChange) onCountryChange(selectedCountry);
  }, [selectedCountry]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (onUseCaseChange) onUseCaseChange(useCase);
  }, [useCase, onUseCaseChange]);

  useEffect(() => {
    if (searchMode !== 'zone') return;
    const apiBase = import.meta.env.VITE_API_URL || '';
    fetch(`${apiBase}/api/v1/sites/zone-municipalities?country=${selectedCountry}`)
      .then(r => r.json())
      .then(data => setMunicipalityList(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [searchMode, selectedCountry]);
  // Coordinate-input sidebar row was removed — users select parcels
  // by clicking on the map directly (SiteScreener.handleMapClick has
  // hit-tested viewport parcels for ages). The backend endpoint
  // /api/v1/layers/parcel-at-point and SiteScreener.lookupParcelAtCoordinates
  // are kept intact so a future map-overlay search box (à la Google
  // Maps) can wire to them without backend changes.

  // Tenant siting profile (2026-10-07): branding.siting_profile from
  // /orgs/current, resolved server-side (services/siting_profiles.py,
  // docs/tenant-siting-profiles.md). plant === 'ammonia' turns on the
  // ammonia plant model below; search defaults, filter groups and labels
  // are read from the profile. Tenants without a profile see the default
  // panel for their use case.
  const sitingProfile = branding?.siting_profile || null;
  const isAmmonia = sitingProfile?.plant === 'ammonia' && useCase === (sitingProfile?.use_case || 'hydrogen');
  const [maxResidents2km, setMaxResidents2km] = useState(null);      // null = no filter
  const [requireHazmatPort, setRequireHazmatPort] = useState(false);
  const [requireIndustrialZoning, setRequireIndustrialZoning] = useState(false);
  // Include future zoning (2026-10-17): 'off' keeps land an adopted regulatory
  // plan allows; 'A' adds land the master plan earmarks for næring/industri
  // with no regulatory plan yet; 'AB' adds plan proposals with such land use.
  const [futureZoning, setFutureZoning] = useState('off');
  const [priceZones, setPriceZones] = useState([]);                  // [] = any
  const [maxCoastKm, setMaxCoastKm] = useState(null);
  // Harbour + capacity screen: start from grid points with published
  // headroom, then harbours, then homes; parcels are clipped to the land
  // that meets every criterion.
  const [harbourScreenOn, setHarbourScreenOn] = useState(false);
  const [screenTest, setScreenTest] = useState('t1');
  const [screenCapacityKm, setScreenCapacityKm] = useState(10);
  const [screenHarbourKm, setScreenHarbourKm] = useState(1);
  const [screenNeighbourM, setScreenNeighbourM] = useState(500);
  const [screenReportedQueueOnly, setScreenReportedQueueOnly] = useState(false);
  // The screen is a Norwegian method for hydrogen / ammonia plants; for
  // other tenants the toggle is not shown and nothing is sent.
  const screenActive = harbourScreenOn && selectedCountry === 'NO' && useCase === 'hydrogen';
  // Profile search defaults: applied once per profile, after it has arrived
  // and while the panel is on the use case it applies to. The user can
  // still move every slider afterwards.
  const profileDefaultsApplied = useRef(null);
  useEffect(() => {
    const d = sitingProfile?.search_defaults;
    const pid = sitingProfile?.id || 'profile';
    if (!d || profileDefaultsApplied.current === pid) return;
    if (useCase !== (sitingProfile.use_case || 'hydrogen')) return;
    profileDefaultsApplied.current = pid;
    if (d.max_port_distance_m != null) setMaxPortDistance(Number(d.max_port_distance_m));
    if (d.max_slope_pct != null)       setMaxSlopePct(Number(d.max_slope_pct));
    if (d.max_elevation_m != null)     setMaxElevationM(Number(d.max_elevation_m));
    if (d.max_buildings_on_site !== undefined) setMaxBuildingsOnSite(d.max_buildings_on_site === null ? null : Number(d.max_buildings_on_site));
    // harbour_screen: 't1' | 't2' | 't3' starts the panel on the brief's
    // method (published grid capacity → harbour → homes) with that test.
    if (d.harbour_screen && SCREEN_TESTS[d.harbour_screen]) {
      setHarbourScreenOn(true);
      setScreenTest(d.harbour_screen);
    }
  }, [sitingProfile, useCase]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Restore the last completed search on mount — recovers from browser
  // tab discard / reload (background memory saver kicks in around 5 min
  // out on mobile + Chrome's "memory saver" mode on desktop). TTL caps
  // staleness so we don't show day-old results. Silent restore: the
  // user sees results without having pressed Find, which is itself
  // the cue that this is the previous session. Waits for the tenant's
  // branding: its country and use case decide whether the saved set applies.
  const lastSearchRestored = useRef(false);
  useEffect(() => {
    if (orgLoading || lastSearchRestored.current) return;
    lastSearchRestored.current = true;
    try {
      const store = _lastSearchStore();
      const raw = store?.getItem(LAST_SEARCH_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      const age = Date.now() - (data?.savedAt || 0);
      // Country + use-case guard: a NO search must not resurrect over a
      // FI session, nor an ammonia result set over a data-center one.
      // Missing fields on old blobs → treat as stale.
      if (age > LAST_SEARCH_TTL_MS || data?.country !== selectedCountry
          || (data?.useCase && data.useCase !== useCase)) {
        store?.removeItem(LAST_SEARCH_KEY);
        return;
      }
      if (Array.isArray(data.results) && data.results.length > 0) {
        setTeigResults(data.results);
        setTeigExcluded(Array.isArray(data.excluded) ? data.excluded : []);
        setTeigHardExcluded(Array.isArray(data.hardExcluded) ? data.hardExcluded : []);
        setTeigHardExcludedSummary(data.hardExcludedSummary || {});
        setTeigFunnel(data.funnel || null);
        setSearchMeta(data.searchMeta || null);
        if (onTeigResultsChange) onTeigResultsChange(data.results);
      }
    } catch {}  // parse error / corrupt entry — silently skip
  // Runs once, when the branding has arrived. onTeigResultsChange is
  // captured from closure; the restored set fires it once which is the
  // right behaviour (parent gets the same notification as a fresh search).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgLoading]);

  // Track mouse position for cursor tooltip
  useEffect(() => {
    const handleMouseMove = (e) => setMousePos({ x: e.clientX, y: e.clientY });
    if (cursorTooltip.visible) {
      window.addEventListener('mousemove', handleMouseMove);
      return () => window.removeEventListener('mousemove', handleMouseMove);
    }
  }, [cursorTooltip.visible]);
  // DC-only filters retained after the 2026-06-03 sidebar cleanup:
  //   max_ixp_distance_km — informational only; sidebar control was
  //                    removed earlier (Helgeland is a fibre desert).
  //
  // Cut in the 2026-06-03 cleanup, see commit log for rationale:
  //   minFreeCoolingDays    — proxy data; moved to per-site display
  //   maxUrbanDistanceKm    — measured tettsteder w/o pop threshold
  //   minResidentialDistanceM — NO data source doesn't exist
  //   gridRedundancy        — most users didn't gate on it; backend
  //                           still honours require_dual_feed if set
  const [maxIxpDistanceKm, setMaxIxpDistanceKm] = useState(null);
  // Fiber-route distance filter. null = Any (off). Buckets: 1, 5, 10,
  // 25 km. Buckets, not slider, because fiber-route distance has hard
  // cost cliffs (sub-1 km = no spur; 1-5 km = trench but feasible;
  // 5-25 km = significant spend; >25 km = effectively start from
  // scratch). Sliders push users toward "10 km, why not 12" — buckets
  // make them think about which cliff they accept.
  const [maxFiberDistanceKm, setMaxFiberDistanceKm] = useState(null);
  // Min readiness score filter removed per user feedback — composite
  // scores were misleading when upstream data feeds were rate-limited.
  // Backend still computes the score (used for DC sort order) but
  // there's no UI to gate on it.

  // Project parameters
  const [technology, setTechnology] = useState('PEM');
  const [powerCapacityMw, setPowerCapacityMw] = useState(20);

  // Capacity-first search (audit 2026-06-01, Option B consolidated).
  // Single env-gated flow — no UI toggle. When the flag is on, the
  // "Find sites" button calls /api/v1/search/confirmed-capacity and
  // renders ConfirmedCapacityResults; when off, the legacy parcel-
  // first /sites/search SSE pipeline runs unchanged. The flag is the
  // sole rollback mechanism until the new flow is validated in prod.
  // URL escape hatch (2026-06-03): append ?capacity_first=off to the
  // page URL to bypass the capacity-first chain and run a plain
  // voltage-based /sites/search SSE for the visible bbox. Useful for
  // A/B comparing "what the substation classifier hides vs surfaces"
  // without touching env vars or redeploying. Default (no URL param)
  // honours VITE_FEATURE_SEARCH_MODE_SELECTOR.
  let _capacityFirstDisabledByUrl = false;
  try {
    const _qs = new URLSearchParams(window.location.search);
    _capacityFirstDisabledByUrl = _qs.get('capacity_first') === 'off';
  } catch { /* SSR / no-window — ignore */ }
  const CAPACITY_FIRST_ENABLED =
    !_capacityFirstDisabledByUrl &&
    (import.meta.env.VITE_FEATURE_SEARCH_MODE_SELECTOR || '').toLowerCase() === 'true';
  if (_capacityFirstDisabledByUrl) {
    console.log('[capacity-first] DISABLED via ?capacity_first=off — using plain voltage-based SSE search');
  }
  // /confirmed-capacity response — set immediately after phase 1 so
  // the substation cards + inquiry list paint before the SSE chain
  // returns parcels. Cleared on every new search.
  const [capacityFirstResponse, setCapacityFirstResponse] = useState(null);
  // 'idle' | 'phase1' (capacity API in flight) | 'phase2' (SSE in flight) | 'complete'
  const [capacityFirstStatus, setCapacityFirstStatus] = useState('idle');
  // Cooling strategy for DC mode (audit 2026-05-27 — replaced bare
  // PUE selector). Each strategy maps to a representative PUE which
  // is then climate-adjusted downstream if free-cooling days are
  // known. Default = Free cooling DX, the right answer for Norway
  // (235+ free-cooling days/yr typical). Previous default of 1.4
  // (traditional) was conservative to the point of misleading.
  const [coolingStrategy, setCoolingStrategy] = useState('free_cooling');
  // Audit 2026-05-27: site size is now user-tweakable. Stored as
  // an integer hectare value — null means "derive from MW × 0.6
  // recommended ratio". Three presets (Tight 0.4 / Standard 0.6 /
  // Hyperscale 1.0) snap to those ratios; the input lets the user
  // type any custom number on top.
  const [siteHaOverride, setSiteHaOverride] = useState(null);
  const COOLING_OPTIONS = [
    { value: 'liquid',         pue: 1.10, label: 'Liquid cooling',       range: 'PUE 1.08–1.12' },
    { value: 'free_cooling',   pue: 1.18, label: 'Free cooling DX',      range: 'PUE 1.15–1.20 · Recommended for Norway' },
    { value: 'air_economizer', pue: 1.25, label: 'Air with economiser',  range: 'PUE 1.20–1.30' },
    { value: 'traditional',    pue: 1.40, label: 'Traditional air',      range: 'PUE 1.35–1.45' },
  ];
  const dcPue = COOLING_OPTIONS.find(o => o.value === coolingStrategy)?.pue ?? 1.18;

  // Site size auto-tracks MW × 0.6. When the user changes MW, any
  // manual ha override is cleared — a 30 ha override for a 20 MW
  // project is no longer meaningful once they switch to 100 MW. The
  // override stays sticky only while MW is constant.
  React.useEffect(() => {
    setSiteHaOverride(null);
  }, [powerCapacityMw]);

  // bufferM removed from the UI — the picker (250/500/750/1000/1500) wasn't
  // meaningfully changing screening output. Backend default of 500 m is now
  // hardcoded inline in the two findTeiger / smart-search payloads below.

  // Filter-dirty tracking. When the user runs a search we snapshot the
  // current filter values; on every render we recompute the snapshot
  // and compare. If they differ AND we have results showing, render
  // the "Filters changed — re-run search" banner so the user doesn't
  // mistake stale results for fresh ones.
  const [lastSearchFilterKey, setLastSearchFilterKey] = useState(null);
  const [hasSeawaterAccess, setHasSeawaterAccess] = useState(false);

  // Hard filters
  // Max grid distance follows the same suggest-then-override pattern as
  // voltage: default tracks project size (the function buckets by
  // TOTAL power draw = IT load × PUE, since that's what the grid
  // actually sees). Larger projects amortise spur-line CAPEX over
  // more MW so further substations stay economical. Effect below
  // resets the override when MW or cooling-strategy changes.
  const totalPowerDrawMw = powerCapacityMw * dcPue;
  const suggestedGridDistance = useMemo(
    () => suggestedGridDistanceKm(totalPowerDrawMw),
    [totalPowerDrawMw],
  );
  const [gridDistanceOverride, setGridDistanceOverride] = useState(null);
  const maxGridDistance = gridDistanceOverride != null
    ? gridDistanceOverride : suggestedGridDistance;
  React.useEffect(() => {
    setGridDistanceOverride(null);
  }, [powerCapacityMw, coolingStrategy]);
  const setMaxGridDistance = (v) => setGridDistanceOverride(v);
  // minVoltage is derived from project MW by default, but the user can
  // override to any of the standard transmission tiers (22/66/132/300/
  // 420 kV) — same suggest-then-override pattern as siteHaOverride.
  // Effect below resets the override when MW changes, so the field
  // always reflects the current project unless the user actively re-
  // selected after the MW change.
  const VOLTAGE_TIERS = [22, 66, 132, 300, 420];
  const [voltageOverride, setVoltageOverride] = useState(null);
  const suggestedVoltage = useMemo(
    () => requiredVoltage(powerCapacityMw),
    [powerCapacityMw],
  );
  const minVoltage = voltageOverride != null ? voltageOverride : suggestedVoltage;
  React.useEffect(() => {
    setVoltageOverride(null);
  }, [powerCapacityMw]);
  // Protected-area distance and port depth left the panel on 2026-10-14
  // (the SSE search never applied either); the legacy grid-first
  // findSites still reads these values.
  const minProtectedDistance = 1.0;
  const [maxPortDistance, setMaxPortDistance] = useState(50000); // meters
  const minPortDepth = 6.0;
  const [maxWaterDistance, setMaxWaterDistance] = useState(15);
  // Water source type filter — multi-select. Empty array = "any source".
  // Types: 'lake' (innsjø), 'river' (elv), 'sea' (saltwater / fjord / port).
  const [waterSourceTypes, setWaterSourceTypes] = useState(['lake', 'river', 'sea']);
  // Whether water access is REQUIRED at all (DC use case). Default off:
  // for free-cooling DX the make-up water need is small enough that
  // distance to source isn't a screening gate for most projects. User
  // opts in if their cooling strategy or compliance needs make water
  // proximity a real constraint. Hydrogen always treats water as
  // required since it's the electrolyser feedstock.
  const [waterAccessRequired, setWaterAccessRequired] = useState(false);

  // Results view toggle — "accepted" shows sites that passed all filters;
  // "rejected" shows the hard-excluded sites grouped by reason, with each
  // rejection expandable so the user can still inspect them on the map.
  const [resultsTab, setResultsTab] = useState('accepted'); // 'accepted' | 'rejected'
  const [expandedReasons, setExpandedReasons] = useState(new Set());
  const [maxElevationM, setMaxElevationM] = useState(800);
  // Slope default 35% matches HARD_MAX_SLOPE_PCT — values below 35
  // Max slope threshold for "buildable" — controls both the
  // screening centroid filter and the precise DEM compute that
  // runs on final candidates. UI re-added 2026-06-04 (was hidden
  // at 35% while precise was hardcoded to 15%, causing the
  // screening / precise divergence). Default 20% — covers most
  // economically-graded industrial sites; Vestlandet users can
  // dial up to 30%; flat-terrain users can tighten to 10%.
  const [maxSlopePct, setMaxSlopePct] = useState(20);

  // Terrain defaults are country-specific. Norway has high mountains
  // (Galdhøpiggen 2469 m, many DC-relevant valleys at 200-600 m), so
  // 800 m catches roughly the whole built-up zone. Finland is
  // structurally flat (Halti 1324 m is the only point > 800, and no
  // sane DC site is above ~300 m), so the 800 m default is effectively
  // a no-op filter there. Reset to FI-appropriate values when the
  // country flips. User can still slide higher if they want.
  useEffect(() => {
    if (selectedCountry === 'FI') {
      setMaxElevationM(300);
      setMaxSlopePct(5);
    } else {
      // A tenant profile may override the Norwegian defaults (ammonia:
      // 150 m and 10 %, since the plant sits low and needs flat ground).
      const d = isAmmonia ? (sitingProfile?.search_defaults || {}) : {};
      setMaxElevationM(d.max_elevation_m != null ? Number(d.max_elevation_m) : 800);
      setMaxSlopePct(d.max_slope_pct != null ? Number(d.max_slope_pct) : 20);
    }
  }, [selectedCountry, isAmmonia, sitingProfile]);

  // Default to ≤5 buildings (not null/Any). Stops the engine from
  // returning aggregated "sites" stitched together from 69 dense
  // residential parcels — buyers can't actually build on those.
  // Anyone wanting unrestricted dense-area results can flip to Any.
  const [maxBuildingsOnSite, setMaxBuildingsOnSite] = useState(5);
  const [sortBy, setSortBy] = useState('voltage_level');
  const [sortMode, setSortMode] = useState('voltage'); // 'voltage' | 'distance' | 'port' | 'water' | 'protected'
  const [resultLimit, setResultLimit] = useState(50);

  // Advanced filters — preset-based (API-based, opt-in)
  // Each preset maps to backend filter values; 'any' = no filter applied
  // Landslide & flood (no option changed the result) and build season
  // (months from latitude bands, not MET data) left the panel on
  // 2026-10-14; the legacy findSites still reads them.
  const hazardPreset = 'any';
  const [speciesPreset, setSpeciesPreset] = useState('any');
  const [roadPreset, setRoadPreset] = useState('any');
  // Max road distance — straight-line distance from parcel centroid
  // to nearest NVDB road segment. Buckets, not slider — last-mile road
  // costs scale with the type of build (paved access spur vs gravel
  // logging road vs none). Default Any = off; user opts in.
  const [maxRoadDistanceKm, setMaxRoadDistanceKm] = useState(null);
  const [bridgePreset, setBridgePreset] = useState('any');
  const [solarPreset, setSolarPreset] = useState('any');
  const buildSeasonPreset = 'any';
  const [waterRightsPreset, setWaterRightsPreset] = useState('any');
  // Prescreen is computed automatically: check 3× more candidates than you want results,
  // minimum 100 — users don't need to tune this manually

  // ── Telemetry ──────────────────────────────────────────────────────────
  // Snapshot of every user-controlled hard filter at the moment of an
  // event (search completion or project save). Distribution analysis
  // joins these against the suggester defaults to determine "at default
  // vs custom" for the 8-week prune decision (cut threshold: <5% search
  // usage AND <10% saved-project usage). Adding/removing a filter only
  // touches this object and the backend schema's `filters_active` is
  // a loose dict — no migration needed.
  const snapshotActiveFilters = useCallback(() => ({
    use_case: useCase,
    technology,
    power_capacity_mw: powerCapacityMw,
    cooling_strategy: coolingStrategy,
    // Suggest-then-override trio — both value and "is overridden" so the
    // distribution analysis can split "users at default" vs "users
    // actively picking" without re-deriving the default per-row.
    min_voltage_kv: minVoltage,
    min_voltage_overridden: voltageOverride != null,
    max_grid_distance_km: maxGridDistance,
    max_grid_distance_overridden: gridDistanceOverride != null,
    // Plain hard filters
    max_buildings_on_site: maxBuildingsOnSite,
    max_slope_pct: maxSlopePct,
    max_elevation_m: maxElevationM,
    min_protected_distance_km: minProtectedDistance,
    max_water_distance_km: maxWaterDistance,
    water_access_required: waterAccessRequired,
    water_source_types: waterSourceTypes,
    // Preset selectors
    hazard_preset: hazardPreset,
    species_preset: speciesPreset,
    road_preset: roadPreset,
    bridge_preset: bridgePreset,
    build_season_preset: buildSeasonPreset,
    water_rights_preset: waterRightsPreset,
  }), [
    useCase, technology, powerCapacityMw, coolingStrategy,
    minVoltage, voltageOverride, maxGridDistance, gridDistanceOverride,
    maxBuildingsOnSite, maxSlopePct, maxElevationM,
    minProtectedDistance, maxWaterDistance, waterAccessRequired, waterSourceTypes,
    hazardPreset, speciesPreset, roadPreset, bridgePreset,
    buildSeasonPreset, waterRightsPreset,
  ]);

  const emitTelemetry = useCallback((payload) => {
    // Fire-and-forget. Backend returns 204; any failure is non-fatal.
    try {
      const apiBase = import.meta.env.VITE_API_URL || '';
      fetch(`${apiBase}/api/v1/search/telemetry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } catch (e) {
      console.warn('[telemetry] emit failed:', e);
    }
  }, []);

  // Sync filter criteria to SiteScreener for teig green/red classification.
  // NOTE: Phase 1 (find-sites API) only filters substations by min_voltage_kv.
  // All other criteria here are TEIG-LEVEL — they control the green/red display
  // of cadastral parcels in SiteScreener (Phase 2), not the substation search.
  // The backend receives all fields but ignores teig-level ones in Phase 1 SQL.
  useEffect(() => {
    if (!onCriteriaChange) return;
    onCriteriaChange({
      // Substation-level (used by Phase 1 SQL)
      minVoltage,
      // Teig-level (used by Phase 2 green/red teig classification only)
      maxGridDistance,
      minAreaHa: calcAreaRequirement(powerCapacityMw, technology).totalHa,
      minProtectedDistance,
      maxPortDistance,
      minPortDepth,
      maxWaterDistance,
      maxSlopePct,
      maxElevationM,
    });
  }, [maxGridDistance, minVoltage, powerCapacityMw, technology, minProtectedDistance, maxPortDistance, minPortDepth, maxWaterDistance, maxSlopePct, maxElevationM]); // eslint-disable-line react-hooks/exhaustive-deps

  // Count active advanced filters for badge
  const advancedActiveCount = [
    hazardPreset, speciesPreset, roadPreset, bridgePreset, buildSeasonPreset, waterRightsPreset,
  ].filter(p => p !== 'any').length;

  const runZoneChecks = useCallback(async (sites, onFirstCovered) => {
    if (!sites?.length) return;
    setZoneChecking(true);
    setZoneChecks({});
    let firstCoveredNotified = false;
    const BATCH = 5;
    for (let start = 0; start < sites.length; start += BATCH) {
      const batch = sites.slice(start, start + BATCH);
      await Promise.allSettled(batch.map(async (site, bi) => {
        const idx = start + bi;
        const lng = site.location?.longitude;
        const lat = site.location?.latitude;
        if (lng == null || lat == null) {
          setZoneChecks(prev => ({ ...prev, [`${idx}`]: false }));
          return;
        }
        const has = await checkDibkAtCoords(lng, lat);
        setZoneChecks(prev => ({ ...prev, [`${idx}`]: has }));
        if (has && !firstCoveredNotified && onFirstCovered) {
          firstCoveredNotified = true;
          onFirstCovered(site);
        }
      }));
    }
    setZoneChecking(false);
  }, []);

  const findSites = async () => {
    if (!searchAllNorway && !mapBounds) {
      setError('Zoom inn til et område på kartet, eller velg "Hele Norge"');
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (onSearchStart) onSearchStart();
    setIsSearching(true);
    setError(null);
    setResults(null);
    setZoneChecks({});

    try {
      // Map presets → backend filter values
      // Hazard: basic = exclude overlapping zones only, safe = ≥1km, strict = ≥2km
      const hazardMap = { any: [false, null], basic: [true, null], safe: [true, 4], strict: [true, 5] };
      // Species: no-salmon = excl. salmon rivers only, low-conflict = no EN/CR (score ≥3), strict = no red-listed (score ≥5)
      const speciesMap = { any: [false, null], 'no-salmon': [true, null], 'low-conflict': [true, 3], strict: [true, 5] };
      // Road: kommunevei = municipal or better (score 2), fylkesvei = county or better (score 3), national = E/Rv only (score 5)
      const roadMap = { any: null, kommunevei: 2, fylkesvei: 3, national: 5 };
      // Bridge: thresholds based on equipment weight — standard trucks, electrolysers, transformers
      const bridgeMap = { any: null, light: 40, standard: 80, heavy: 120 };
      const solarMap = { any: null, moderate: 750, good: 850, high: 950 };
      // Build season: realistic Norway ranges — sub-arctic (5), continental (7), coastal south (9)
      const buildMap = { any: null, short: 5, long: 7, yearround: 9 };
      // Water rights: NVE Verneplan (protected watersheds) + Vannkraft (hydro concessions)
      const waterMap = {
        any:      { excl_verneplan: false, excl_hydro: false },
        basic:    { excl_verneplan: true,  excl_hydro: false },
        strict:   { excl_verneplan: true,  excl_hydro: true  },
      };

      const [excl_hazard, min_hazard] = hazardMap[hazardPreset] || [false, null];
      const [excl_salmon, min_species] = speciesMap[speciesPreset] || [false, null];
      const waterOpts = waterMap[waterRightsPreset] || waterMap.any;

      const filters = {
        max_grid_distance_km: maxGridDistance,
        min_voltage_kv: minVoltage,
        min_protected_distance_km: minProtectedDistance,
        max_port_distance_km: useCase !== 'data_center' ? maxPortDistance / 1000 : undefined,
        min_port_depth_m: useCase !== 'data_center' ? minPortDepth : undefined,
        // Water distance: hydrogen always filters (electrolyser feedstock);
        // DC only filters when the user opts in via waterAccessRequired.
        // Omitting the field entirely means the backend doesn't gate on
        // water distance at all for that site.
        ...((useCase !== 'data_center' || waterAccessRequired)
            ? { max_water_distance_km: maxWaterDistance } : {}),
        // Only send the type filter when the user has narrowed it from "all".
        // Empty array OR all 3 selected = no filter (matches any source).
        ...(waterSourceTypes.length > 0 && waterSourceTypes.length < 3
            ? { water_source_types: waterSourceTypes } : {}),
        exclude_hazard_zones: excl_hazard,
        exclude_salmon_rivers: excl_salmon,
        exclude_protected_waterways: waterOpts.excl_verneplan,
        exclude_hydro_concessions: waterOpts.excl_hydro,
        max_slope_pct: maxSlopePct,
        max_elevation_m: maxElevationM,
        // DC-specific hard filters
        // Cut in the 2026-06-03 cleanup: min_free_cooling_days (proxy
        // data — moved to per-site display with verification disclaimer),
        // max_urban_distance_km (tettsteds had no population threshold),
        // min_residential_distance_m (no NO data source).
        // Backend still accepts these params for legacy clients but the
        // UI no longer surfaces them.
        ...(useCase === 'data_center' && maxIxpDistanceKm !== null ? { max_ixp_distance_km: maxIxpDistanceKm } : {}),
        ...(maxFiberDistanceKm !== null ? { max_fiber_distance_km: maxFiberDistanceKm } : {}),
        ...(maxRoadDistanceKm !== null ? { max_road_distance_km: maxRoadDistanceKm } : {}),
        // Grid redundancy filter cut 2026-06-03. Backend still accepts
        // require_dual_feed / min_redundancy_voltage_kv if a caller
        // sends them; the UI no longer surfaces the control.
      };

      if (min_hazard !== null) filters.min_hazard_score = min_hazard;
      if (min_species !== null) filters.min_species_score = min_species;
      if (roadMap[roadPreset] !== null) filters.min_road_access_score = roadMap[roadPreset];
      if (bridgeMap[bridgePreset] !== null) filters.min_bridge_capacity_tonnes = bridgeMap[bridgePreset];
      if (solarMap[solarPreset] !== null) filters.min_solar_irradiation = solarMap[solarPreset];
      if (buildMap[buildSeasonPreset] !== null) filters.min_construction_season_months = buildMap[buildSeasonPreset];

      // Compute bounding box.
      //   - Non-premium        → clamp to viewport ∩ Helgeland (UX guard;
      //                          backend re-clamps for security). Grid-first
      //                          is locked from the free-tier UI, but defend
      //                          in depth in case a path reaches it.
      //   - FI + kunta picked  → leave bounds=null and send
      //                          municipality_numbers; backend resolves the
      //                          true bbox via Tilastokeskus WFS (single
      //                          source of truth, no hand-typed coords)
      //   - NO + map zoomed    → use the current map viewport
      //   - default            → null (search the whole country)
      let searchBounds = null;
      if (!isPremium) {
        // Free tier: clamp to viewport ∩ Helgeland. If the viewport is
        // entirely outside Helgeland (intersection is null) OR mapBounds
        // hasn't been initialised yet, fall back to the whole Helgeland
        // bbox so we never send null (which the backend reads as
        // "search everywhere" — would silently violate the free-tier
        // scope).
        searchBounds = (mapBounds && intersectBbox(mapBounds, FREE_TIER_BBOX)) || FREE_TIER_BBOX;
      } else if (selectedCountry === 'FI' && selectedMunicipalities.length > 0) {
        // Backend handles the kunta → bbox lookup. Keep searchBounds=null.
      } else if (!searchAllNorway && mapBounds) {
        searchBounds = {
          min_lat: mapBounds.min_lat,
          max_lat: mapBounds.max_lat,
          min_lon: mapBounds.min_lon,
          max_lon: mapBounds.max_lon,
        };
      }

      // Map client sortMode to backend sort_by key
      const sortModeToBackend = {
        voltage: 'voltage_level', distance: 'grid_distance',
        port: 'port_distance', water: 'water_distance', protected: 'protected_distance',
      };
      const requestBody = {
        bounds: searchBounds,
        filters,
        project_params: {
          power_capacity_mw: powerCapacityMw,
          technology,
          buffer_m: 500,
          has_seawater_access: hasSeawaterAccess,
        },
        sort_by: sortModeToBackend[sortMode] || 'voltage_level',
        limit: resultLimit,
        prescreen_limit: Math.max(resultLimit * 3, 100),
        country: selectedCountry,
        use_case: useCase,
      };

      const controller = new AbortController();
      abortControllerRef.current = controller;
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const apiBase = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${apiBase}/api/v1/sites/find-sites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      abortControllerRef.current = null;

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      setResults(data);
      setExcludedSites(data.excluded_sites || []);
      if (onResultsChange && data.sites?.length > 0) onResultsChange(data.sites);
      const projectParams = { powerMw: powerCapacityMw, technology, hasSeawaterAccess, country: selectedCountry, useCase, waterAccessRequired, maxWaterDistance };
      // If searching Finland and map is still centered on Norway, fly to first result
      if (selectedCountry === 'FI' && data.sites?.length > 0 && onSubstationPan) {
        onSubstationPan(data.sites[0]);
      }
      if (onlyZoneCoverage && data.sites?.length > 0) {
        // Don't auto-select yet — wait for first covered site from zone checks
        runZoneChecks(data.sites, (site) => {
          onSiteSelect(site, projectParams);
          setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
        });
      } else if (data.sites?.length > 0) {
        if (onSubstationPan) onSubstationPan(data.sites[0]);
        setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(`Search failed: ${err.message}`);
      }
    } finally {
      setIsSearching(false);
    }
  };

  const findZones = async () => {
    if (onSearchStart) onSearchStart();
    setIsSearching(true);
    setError(null);
    setZoneResults(null);
    setResults(null);

    if (selectedMunicipalities.length === 0 && (searchAllNorway || !mapBounds)) {
      setError('Velg minst én kommune, eller bruk "Synlig region" på kartet');
      setIsSearching(false);
      return;
    }

    const params = new URLSearchParams({
      min_voltage_kv: minVoltage,
      max_grid_distance_km: maxGridDistance,
      min_area_ha: 1.0,
      limit: resultLimit,
    });
    if (selectedMunicipalities.length > 0) {
      params.set('municipalities', selectedMunicipalities.join(','));
    } else if (!searchAllNorway && mapBounds) {
      params.set('min_lat', mapBounds.min_lat);
      params.set('max_lat', mapBounds.max_lat);
      params.set('min_lon', mapBounds.min_lon);
      params.set('max_lon', mapBounds.max_lon);
    }

    try {
      const apiBase = import.meta.env.VITE_API_URL || '';
      const r = await fetch(`${apiBase}/api/v1/sites/find-zones?${params}`);
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`HTTP ${r.status}: ${t}`);
      }
      const data = await r.json();
      setZoneResults(data);
      if (data.zones?.length > 0 && onSubstationPan) {
        onSubstationPan(zoneToSite(data.zones[0]));
      }
    } catch (err) {
      setError(`Zone search failed: ${err.message}`);
    } finally {
      setIsSearching(false);
    }
  };

  // ── Smart search (grid-scan) ─────────────────────────────────────────────
  const findSmart = async () => {
    if (onSearchStart) onSearchStart();
    setSmartSearching(true);
    setSmartResults(null);
    setError(null);
    setProgressLog([{ stage: 'grid', label: 'Querying suitability grid…' }]);

    try {
      const apiBase = import.meta.env.VITE_API_URL || '';
      // Use map bounds if available, otherwise municipality bounds.
      // Non-premium: clamp to viewport ∩ Helgeland. (Smart-search is
      // locked from the UI for free tier; this is defense in depth.)
      let searchBbox = null;
      if (!isPremium) {
        // Same fallback semantics as findTeiger — never send a bbox
        // that's outside Helgeland for non-premium callers.
        const b = (mapBounds && intersectBbox(mapBounds, FREE_TIER_BBOX)) || FREE_TIER_BBOX;
        searchBbox = `${b.min_lon},${b.min_lat},${b.max_lon},${b.max_lat}`;
      } else if (selectedMunicipalities.length > 0) {
        // Let the backend resolve — but grid-search needs an explicit bbox.
        // Use a wide Finnish default and let the grid's spatial index handle it.
        searchBbox = '19.0,59.5,31.6,70.1';
      } else if (mapBounds) {
        searchBbox = `${mapBounds.min_lon},${mapBounds.min_lat},${mapBounds.max_lon},${mapBounds.max_lat}`;
      } else {
        searchBbox = '19.0,59.5,31.6,70.1';
      }

      const params = new URLSearchParams({
        bbox: searchBbox,
        min_score: '0.3',
        min_area_ha: String(Math.max(1, (powerCapacityMw || 10) * 0.2)),
        limit: String(resultLimit),
      });

      const resp = await fetch(`${apiBase}/api/v1/sites/grid-search?${params}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();

      setSmartResults(data);
      setProgressLog([
        { stage: 'grid', label: `${data.grid_stats.total_cells.toLocaleString()} cells evaluated` },
        { stage: 'exclude', label: `${data.grid_stats.excluded_cells.toLocaleString()} excluded (${data.grid_stats.pct_excluded}%)` },
        { stage: 'viable', label: `${data.grid_stats.viable_cells.toLocaleString()} viable cells` },
        { stage: 'complete', label: `${data.clusters_found} site zones found in ${data.elapsed_s}s` },
      ]);
    } catch (err) {
      setError(`Smart search failed: ${err.message}`);
    } finally {
      setSmartSearching(false);
    }
  };

  // ── Capacity-first chain ─────────────────────────────────────────────
  // Two-phase search: first POST /confirmed-capacity to rank substations
  // by published headroom; then trigger the existing SSE search inside
  // the union of viable-substation reach circles, grouping streamed
  // parcels under their nearest substation by haversine proximity.
  // Drops parcels whose nearest viable substation is outside its reach
  // (the spec's "don't dilute the point of the mode" rule).
  //
  // Gated on `CAPACITY_FIRST_ENABLED && selectedCountry === 'NO'`. The
  // upstream /assess-station only covers Norway, and the inquiry-portal
  // template links Statnett — so capacity-first is NO-only until the
  // FI equivalent exists.

  const _haversineKm = (lat1, lng1, lat2, lng2) => {
    const R = 6371.0;
    const toRad = d => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat/2)**2 +
              Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng/2)**2;
    return 2 * R * Math.asin(Math.sqrt(a));
  };

  const _unionReachBbox = (substations) => {
    // Returns {min_lat, min_lon, max_lat, max_lon} — the dict shape
    // mapBounds uses throughout this codebase, so it can be dropped
    // into findTeiger's bounds parameter unchanged. Padded by each
    // substation's reach_radius_km so the SSE parcel scan only runs
    // where capacity has been confirmed.
    let minLat =  Infinity, minLng =  Infinity;
    let maxLat = -Infinity, maxLng = -Infinity;
    for (const s of substations) {
      const reachKm = s.reach_radius_km || 5;
      const dLat = reachKm / 111.0;
      const dLng = reachKm / (111.0 * Math.cos(s.lat * Math.PI / 180));
      minLat = Math.min(minLat, s.lat - dLat);
      maxLat = Math.max(maxLat, s.lat + dLat);
      minLng = Math.min(minLng, s.lng - dLng);
      maxLng = Math.max(maxLng, s.lng + dLng);
    }
    return { min_lat: minLat, min_lon: minLng, max_lat: maxLat, max_lon: maxLng };
  };

  // bbox in this codebase travels as the legacy dict
  // {min_lat, min_lon, max_lat, max_lon} (Mapbox-derived). The new
  // /confirmed-capacity endpoint takes the GeoJSON list form
  // [minLon, minLat, maxLon, maxLat]. Single converter so the two
  // shapes never get mixed up in the body.
  const _bboxDictToGeoJsonList = (b) => {
    if (!b) return null;
    if (Array.isArray(b)) return b;
    return [b.min_lon, b.min_lat, b.max_lon, b.max_lat];
  };

  const findCapacityFirstThenSites = async () => {
    if (onSearchStart) onSearchStart();
    setIsSearching(true);
    setError(null);
    setTeigResults(null);
    setTeigExcluded([]);
    setTeigHardExcluded([]);
    setLastSearchFilterKey(currentFilterKey);
    setTeigHardExcludedSummary({});
    setWfsErrors([]);
    setCapacityFirstResponse(null);
    setCapacityFirstStatus('phase1');
    setProgressLog([{
      stage: 'capacity',
      label: 'Checking capacity at substations…',
    }]);

    // Bbox precedence for the capacity-first chain:
    //   non-premium → the free tier's region (?test_region=, RegionLockContext)
    //                 ∩ viewport, else the whole region
    //   premium     → viewport when set, else NO_PREMIUM_DEFAULT_BBOX
    //                 (covers southern Norway — most DC load centres).
    // (The ?test_region= presets for premium users went on 2026-10-14.)
    const _testRegionName = regionLock.locked ? regionLock.region : null;
    const _freeBbox = (regionLock.locked && regionLock.bbox) || FREE_TIER_BBOX;
    const bboxDict = !isPremium
        ? ((mapBounds && intersectBbox(mapBounds, _freeBbox)) || _freeBbox)
        : (mapBounds || NO_PREMIUM_DEFAULT_BBOX);
    const bbox = _bboxDictToGeoJsonList(bboxDict);

    const apiBase = import.meta.env.VITE_API_URL || '';
    const headers = { 'Content-Type': 'application/json' };
    if (isAuthenticated) {
      try {
        const token = await getAccessTokenSilently();
        if (token) headers.Authorization = `Bearer ${token}`;
      } catch (e) {
        // Match findTeiger's behaviour: let the server's 401 surface
        // rather than fail silently.
        console.warn('[capacity-first] getAccessTokenSilently failed:', e);
      }
    }

    let capData;
    try {
      const capResp = await fetch(`${apiBase}/api/v1/search/confirmed-capacity`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          bbox,
          requested_mw: powerCapacityMw,
          // Total grid draw (IT × PUE) for DC; matches what suggestedGridDistanceKm
          // uses to bucket the user's max-grid-distance default. Backend uses this
          // to size each substation's reach circle so reach geometry agrees with
          // the user's own distance filter.
          total_power_draw_mw: totalPowerDrawMw,
          min_voltage_kv: minVoltage,
        }),
      });
      if (!capResp.ok) throw new Error(`Capacity API HTTP ${capResp.status}`);
      capData = await capResp.json();
      setCapacityFirstResponse(capData);
      const meta = capData.search_meta || {};
      const tc = meta.tier_counts || {};
      setProgressLog(prev => [
        ...prev.filter(l => l.stage !== 'capacity'),
        { stage: 'capacity', label:
          `${meta.substations_assessed} substations assessed · `
          + `${tc.sufficient || 0} sufficient · `
          + `${tc.partial || 0} partial · `
          + `${tc.capacity_inquiry || 0} inquiry`,
        },
      ]);
    } catch (err) {
      console.error('[capacity-first] phase 1 failed:', err);
      setError(`Capacity check failed: ${err.message}`);
      setCapacityFirstStatus('idle');
      setIsSearching(false);
      return;
    }

    // Always run the SSE parcel search — sites are the answer; capacity
    // is per-site context. But bbox-tighten to the union of ALL
    // assessed substation reach circles. Rationale: a parcel with no
    // substation within ~4.5 km can't grid-connect for any realistic
    // project, so it contributes nothing to the answer regardless of
    // land quality. Helgeland-wide unfiltered means ~22k parcels and
    // the SSE zone-check phase times out; this cuts it to the parcels
    // that have any chance of working. Still uses ALL substations
    // (not just Sufficient) so the user sees all sites near grid
    // infrastructure, with per-site capacity pills doing the
    // grid-suitability framing.
    // Adaptive substation selection (2026-06-05). Replaces the
    // sequential rung ladder. Pick a mixed-tier set in ONE search
    // sized to the actual tier availability:
    //
    //   Sufficient available:    top 3 sufficient + 1 partial + 1 inquiry
    //   Sufficient absent:       top 5 partial + top 3 inquiry
    //   Both absent:             top 8 inquiry (capacity-drought regions)
    //   None of those:           top 5 voltage_overkill fallback
    //
    // Empirically 5-8 substations consistently yield 15-40 candidates
    // in mixed-tier regions. Below 5 is too dependent on which specific
    // substations the picker happened to choose; above 10 has
    // diminishing returns and the bbox blows out.
    //
    // Geographic constraint OVERRIDES count: 150 km diagonal cap on
    // the union reach bbox. We drop the substation farthest from the
    // centroid of the picked set until we're under the cap (or only
    // 1 left). The geographic guard fires every search now — the
    // 200 km cap from Diag B was effectively a no-op against the
    // matched fallback count.
    //
    // Diagnostic URL params still respected: ?diag_bbox_cap, ?diag_tier_n
    // for tuning experiments without redeploy.
    let _diagBboxCap = 150;
    try {
      const _qs = new URLSearchParams(window.location.search);
      const _bc = parseFloat(_qs.get('diag_bbox_cap'));
      if (Number.isFinite(_bc) && _bc > 0) _diagBboxCap = _bc;
    } catch { /* SSR or sandboxed env */ }
    const SPREAD_DIAGONAL_CAP_KM = _diagBboxCap;
    if (_diagBboxCap !== 150) {
      console.log(`[capacity-first] DIAG override — SPREAD_DIAGONAL_CAP_KM=${_diagBboxCap}`);
    }

    const allSubs = capData.substations || [];
    const sufficient = allSubs.filter(s => s.tier_classification === 'sufficient')
                              .sort((a, b) => (b.composite_score || 0) - (a.composite_score || 0));
    const partial    = allSubs.filter(s => s.tier_classification === 'partial')
                              .sort((a, b) => (b.available_cons_mw || 0) - (a.available_cons_mw || 0));
    const inquiry    = allSubs.filter(s => s.tier_classification === 'capacity_inquiry')
                              .sort((a, b) => (b.composite_score || 0) - (a.composite_score || 0));
    const stepdown   = allSubs.filter(s =>
                                s.tier_classification === 'voltage_overkill' &&
                                (s.available_cons_mw || 0) > 0)
                              .sort((a, b) => (b.available_cons_mw || 0) - (a.available_cons_mw || 0));

    // Disambiguate operator-fallback names when upstream returns
    // name="Unknown" — append voltage + coords so identical
    // operator-named substations are visually distinct in the log.
    const _nameOf = (s) => {
      if (s.name && s.name !== 'Unknown') return s.name;
      const op = s.operator || 'Unknown operator';
      const kv = s.voltage_kv ? `${s.voltage_kv} kV` : '';
      const coord = (Number.isFinite(s.lat) && Number.isFinite(s.lng))
        ? `(${s.lat.toFixed(2)},${s.lng.toFixed(2)})`
        : '';
      return [op, kv, coord].filter(Boolean).join(' ');
    };

    // Bbox diagonal in km — for spread guard + telemetry.
    const _bboxDiagKm = (bb) => {
      if (!bb) return null;
      const latKm = (bb.max_lat - bb.min_lat) * 111;
      const lonKm = (bb.max_lon - bb.min_lon) * 111 *
                    Math.cos(((bb.min_lat + bb.max_lat) / 2) * Math.PI / 180);
      return Math.sqrt(latKm * latKm + lonKm * lonKm);
    };

    // Adaptive picker — choose the mix based on what tiers exist.
    let mode;
    let scoped = [];
    if (sufficient.length > 0) {
      scoped = [
        ...sufficient.slice(0, 3),
        ...partial.slice(0, 1),
        ...inquiry.slice(0, 1),
      ];
      mode = 'sufficient_plus_complement';
    } else if (partial.length > 0) {
      scoped = [
        ...partial.slice(0, 5),
        ...inquiry.slice(0, 3),
      ];
      mode = 'partial_plus_inquiry';
    } else if (inquiry.length > 0) {
      scoped = inquiry.slice(0, 8);
      mode = 'inquiry_only';
    } else if (stepdown.length > 0) {
      scoped = stepdown.slice(0, 5);
      mode = 'voltage_overkill_only';
    } else {
      scoped = allSubs.slice(0, 5);
      mode = 'all_fallback';
    }

    // Dedup by station_id — defensive (a station listed in two tiers
    // would otherwise get its bbox counted twice and inflate spread).
    {
      const _seen = new Set();
      scoped = scoped.filter(s => {
        const k = s.station_id ?? `${s.lat},${s.lng}`;
        if (_seen.has(k)) return false;
        _seen.add(k);
        return true;
      });
    }

    // Spread guard — drop the substation farthest from the centroid
    // until the union reach bbox diagonal is ≤ cap. Geographic
    // constraint overrides the count target per the spec.
    const beforeSpread = scoped.length;
    while (scoped.length > 1) {
      const bb = _unionReachBbox(scoped);
      const diag = _bboxDiagKm(bb);
      if (diag == null || diag <= SPREAD_DIAGONAL_CAP_KM) break;
      const cLat = scoped.reduce((s, x) => s + x.lat, 0) / scoped.length;
      const cLng = scoped.reduce((s, x) => s + x.lng, 0) / scoped.length;
      let worstIdx = 0, worstDist = -1;
      scoped.forEach((s, i) => {
        const d = _haversineKm(s.lat, s.lng, cLat, cLng);
        if (d > worstDist) { worstDist = d; worstIdx = i; }
      });
      scoped.splice(worstIdx, 1);
    }
    const spreadDropped = beforeSpread - scoped.length;

    // Telemetry capture — single-shot now, no per-rung loop.
    const phase2StartMs = Date.now();
    let lastTier = mode;
    let lastSubs = scoped.length;
    let lastBboxDiag = null;
    let lastSpreadCapped = spreadDropped > 0;
    let lastCount = 0, lastWfsErrCount = 0;
    let lastTierCounts = null;

    setCapacityFirstStatus('phase2');
    try {
      if (scoped.length === 0) {
        setProgressLog(prev => [
          ...prev,
          {
            stage: 'parcels',
            label: 'No actionable substations available — skipping parcel scan.',
          },
        ]);
      } else {
        const tightBbox = _unionReachBbox(scoped);
        lastBboxDiag = _bboxDiagKm(tightBbox);
        const _namesList = scoped.map(_nameOf).join(', ');
        const _modeLabel = (
          mode === 'sufficient_plus_complement' ? 'top sufficient + complement' :
          mode === 'partial_plus_inquiry'       ? 'top partial + inquiry' :
          mode === 'inquiry_only'               ? 'top inquiry candidates' :
          mode === 'voltage_overkill_only'      ? 'higher-voltage with capacity (step-down)' :
                                                  'best-available substations'
        );
        setProgressLog(prev => [
          ...prev,
          {
            stage: 'parcels',
            label: spreadDropped > 0
              ? `Searching sites near ${scoped.length} substations · ${_modeLabel} · ${spreadDropped} dropped by 150 km spread guard: ${_namesList}…`
              : `Searching sites near ${scoped.length} substations · ${_modeLabel}: ${_namesList}…`,
          },
        ]);
        const result = await findTeiger({
          boundsOverride: tightBbox,
          keepProgressLog: true,
          holdSearchingFlag: false,
          seedSubstations: scoped.map(s => ({
            station_id: s.station_id,
            name: s.name,
            voltage_kv: s.voltage_kv,
            lat: s.lat,
            lng: s.lng,
            operator: s.operator,
            reach_radius_km: s.reach_radius_km,
          })),
        });
        lastCount = result?.count ?? 0;
        lastWfsErrCount = result?.wfsErrorCount ?? 0;
        lastTierCounts = result?.tierCounts ?? null;
        console.log(`[capacity-first] adaptive (${mode}, ${scoped.length} subs, ${spreadDropped} spread-dropped): count=${lastCount}`);
      }
    } finally {
      setIsSearching(false);
      setCapacityFirstStatus('complete');

      const meta = capData?.search_meta || {};
      emitTelemetry({
        event_type: 'search',
        tier_reached: lastCount > 0 ? lastTier : 'none',
        substations_selected: lastSubs,
        bbox_diag_km: lastBboxDiag != null ? Math.round(lastBboxDiag * 10) / 10 : null,
        spread_capped: lastSpreadCapped,
        parcels_loaded: lastCount,
        phase2_duration_ms: Date.now() - phase2StartMs,
        wfs_error_count: lastWfsErrCount,
        // Option 3 — tier-count breakdown for the 8-week observation.
        // Tells us how often rezoning candidates surface and (combined
        // with save_project events) which tier paying-customers actually
        // shortlist. Empty when the SSE complete didn't include tier_counts
        // (legacy backend / non-NO path).
        tier_counts: lastTierCounts,
        requested_mw: powerCapacityMw,
        total_power_draw_mw: totalPowerDrawMw,
        required_voltage_kv: meta.required_voltage_kv ?? null,
        detected_region: meta.detected_region ?? null,
        filters_active: snapshotActiveFilters(),
        // Adaptive-selection markers (post-2026-06-05). The old per-rung
        // ladder is gone; this records the single picked-set shape so
        // production logs can be grouped by mode + spread-drop count.
        selection_mode: mode,
        adaptive_picked: scoped.length,
        spread_dropped: spreadDropped,
        diag_bbox_diagonal_cap_km: SPREAD_DIAGONAL_CAP_KM,
        diag_overrides_active: SPREAD_DIAGONAL_CAP_KM !== 150,
        // test_region tag so log analysis can split test searches from
        // real production runs. null when no ?test_region= URL param.
        test_region: _testRegionName,
      });
    }
  };

  // Branches between capacity-first chain and legacy parcel-first SSE.
  // Single call site for the search button so the flag is the only thing
  // that decides which flow runs — no in-component mode state.
  const runSearch = async () => {
    if (CAPACITY_FIRST_ENABLED && selectedCountry === 'NO') {
      await findCapacityFirstThenSites();
    } else {
      // Clear capacity-first state in case the user toggled the flag
      // off or switched country mid-session.
      setCapacityFirstResponse(null);
      setCapacityFirstStatus('idle');
      await findTeiger();
    }
  };

  // ── SSE-based teig search ──────────────────────────────────────────────────
  // `boundsOverride` is used by the capacity-first chain to tighten the
  // bbox to the union of viable-substation reach circles; legacy callers
  // pass nothing and the existing bounds precedence applies.
  // `keepProgressLog` preserves phase-1 messages when called from the
  // chain (otherwise the user sees the parcel-stage messages overwrite
  // the substation-stage ones).
  // Returns {count, wfsErrorCount} from the SSE complete event, or
  // null on abort/error. The capacity-first chain uses count to decide
  // whether to escalate, and wfsErrorCount for telemetry. `holdSearchingFlag`
  // keeps isSearching=true across iterations so the search button stays
  // disabled until the chain truly finishes.
  const findTeiger = async ({ boundsOverride = null, keepProgressLog = false, holdSearchingFlag = false, seedSubstations = null, fresh = false } = {}) => {
    lastSearchOptsRef.current = { boundsOverride, seedSubstations };
    if (onSearchStart) onSearchStart();
    setIsSearching(true);
    setError(null);
    let _completeCount = null;   // captured from the SSE complete event; returned to caller
    let _wfsErrorCount = 0;      // captured from the teiger SSE event for telemetry
    let _tierCounts = null;      // {industrial, rezoning} from SSE complete event for telemetry
    setTeigResults(null);
    setTeigExcluded([]);
    setTeigHardExcluded([]);
    // Snapshot the filter values being submitted so any subsequent
    // change to a filter input flips filtersDirty true.
    setLastSearchFilterKey(currentFilterKey);
    setTeigHardExcludedSummary({});
    // Show immediate feedback before SSE connection is established
    setProgressLog([{ stage: 'connecting', label: 'Starting search…' }]);
    // Scroll to progress panel so user sees the status
    setTimeout(() => progressRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    setWfsErrors([]);
    // Show cursor tooltip
    clearTimeout(cursorTooltipTimeoutRef.current);
    setCursorTooltip({ visible: true, text: 'Searching…', fading: false });

    // Labels no longer carry their own ✓/⟳ glyphs — the
    // .progress-dot span in the rendered row is the single
    // visual indicator (✓ for done, · for active). Keeping the
    // glyph in the string too created a double-marker that read
    // as decoration. Labels are now plain text.
    const STAGE_LABELS = {
      substations: (e) => `${e.count} grid connections`,
      screen: (e) => {
        if (e.count == null) return e.message || 'Screening harbours and grid capacity…';
        if (e.reason) return `Screen: ${e.reason}`;
        return `${e.capacity_points_passing} grid points pass the test · ${e.harbours_in_reach} harbours in reach · ${e.zone_kept_ha} ha clear of homes${e.parcel_tiles_truncated ? ` (largest ${e.count} of ${e.parcel_tiles_truncated} areas searched — pick a region for full coverage)` : ''}`;
      },
      screen_parcels: (e) => e.count != null
        ? `${e.count} parcels have land meeting every criterion (of ${e.parcels_in} checked${e.water_dropped ? `, ${e.water_dropped} water dropped` : ''})`
        : (e.message || 'Clipping parcels…'),
      teiger:      (e) => {
        if (e.wfs_errors?.length) {
          setWfsErrors(e.wfs_errors);
          _wfsErrorCount = e.wfs_errors.length;
        }
        const n = e.count != null ? e.count : '?';
        return `${n} parcels loaded${e.wfs_errors?.length ? ` (${e.wfs_errors.length} errors)` : ''}`;
      },
      pre_filter:  (e) => e.count != null
        ? `${e.count} parcels after pre-filter (${e.excluded ?? 0} excluded)`
        : 'Pre-filtering parcels…',
      assembling:  (e) => e.count != null
        ? `${e.count} assemblages formed`
        : 'Building assemblages…',
      slope_filter: (e) => {
        if (e.count == null) return 'Filtering steep terrain (>35%)…';
        const dropped = e.dropped ?? 0;
        const input = e.total_input ?? (e.count + dropped);
        return `${e.count} after slope filter (${dropped} of ${input} dropped — slope > 35%)`;
      },
      zone_check:  (e) => {
        if (e.count == null) return 'Checking zones…';
        const total = e.total_found ?? e.count;
        const dropped = e.dropped ?? 0;
        return `${total} passed zone check${dropped > 0 ? ` — analysing top ${e.count} (${dropped} dropped)` : ''}`;
      },
      enriching:   (e) => e.progress != null
        ? `${t('siteFinder.analyzing')}… (${e.progress}/${e.total})`
        : `${t('siteFinder.fetchingData')}…`,
      complete:    (e) => `${t('siteFinder.complete')} — ${e.total} ${t('siteFinder.suitableSitesFound')}`,
    };

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const bridgeMap = { any: null, light: 40, standard: 80, heavy: 120 };

    const apiBase = import.meta.env.VITE_API_URL || '';
    // Not sent since 2026-10-14: min_protected_distance_km, min_port_depth_m
    // and hazard_level, which the search never applied (only overlap with a
    // protected area excludes; depths are mostly guessed from the port type;
    // flood zones are always excluded), and min_build_season_months, whose
    // months came from latitude bands rather than MET data.
    // backend/scripts/run_preset_search.py sends the same body, so runner
    // and panel share result-cache entries.
    const body = JSON.stringify({
      // fresh: bypass the result cache and overwrite its slot with this run.
      ...(fresh ? { fresh: true } : {}),
      filters: {
        min_voltage_kv: minVoltage,
        max_grid_distance_km: maxGridDistance,
        project_mw: powerCapacityMw,
        technology,
        buffer_m: 500,
        max_port_distance_m: maxPortDistance,
        ...(isAmmonia ? {
          max_residents_2km: maxResidents2km,
          require_hazmat_port: requireHazmatPort,
          require_industrial_zoning: requireIndustrialZoning,
          price_zones: priceZones,
          max_coast_distance_km: maxCoastKm,
          future_zoning: futureZoning,
        } : {}),
        ...(screenActive ? {
          harbour_screen: {
            enabled: true,
            min_available_mw: SCREEN_TESTS[screenTest].min_available_mw,
            max_queue_mw: SCREEN_TESTS[screenTest].max_queue_mw,
            only_reported_queue: screenReportedQueueOnly,
            max_capacity_km: screenCapacityKm,
            max_harbour_km: screenHarbourKm,
            min_neighbour_m: screenNeighbourM,
          },
        } : {}),
        // Water distance: opt-in for DC, always-on for hydrogen.
        ...((useCase !== 'data_center' || waterAccessRequired)
            ? { max_water_distance_km: maxWaterDistance } : {}),
        ...(waterSourceTypes.length > 0 && waterSourceTypes.length < 3
            ? { water_source_types: waterSourceTypes } : {}),
        biodiversity_level: speciesPreset,
        road_quality: roadPreset,
        bridge_capacity_t: bridgeMap[bridgePreset],
        water_rights_level: waterRightsPreset,
        ...(maxBuildingsOnSite !== null ? { max_buildings_on_site: maxBuildingsOnSite } : {}),
        ...(maxFiberDistanceKm !== null ? { max_fiber_distance_km: maxFiberDistanceKm } : {}),
        ...(maxRoadDistanceKm !== null ? { max_road_distance_km: maxRoadDistanceKm } : {}),
        // road_quality is sent in both flows so the DC path picks it up too.
        road_quality: roadPreset,
        // Terrain filters — backend computes slope/elevation per parcel via
        // a 5-point sample (slope_cache_service) during fast-enrichment.
        max_slope_pct: maxSlopePct,
        max_elevation_m: maxElevationM,
        results_count: resultLimit,
      },
      // Bounds priority for the SSE /sites/search endpoint:
      //   1. capacity-first chain passes boundsOverride (union of viable
      //      substation reach circles) — wins over all other rules so
      //      the parcel scan only runs where capacity was confirmed.
      //   2. Non-premium → clamp to viewport ∩ the free region (falls back
      //      to the region's bbox if no viewport yet). UX guard only;
      //      backend must re-clamp / reject for security.
      //   3. Municipalities picked → bounds=null, send municipality_numbers
      //      so the backend resolves the true bbox (Kartverket kommuneinfo
      //      for NO, Tilastokeskus WFS for FI). Until 2026-10-14 a NO pick
      //      kept the map view, which cut out municipalities off-screen.
      //   4. Visible region → map viewport.
      //   5. Default → null (search whole country).
      bounds: boundsOverride
              ? boundsOverride
              : !isPremium
                ? ((mapBounds && intersectBbox(mapBounds, FREE_TIER_BBOX)) || FREE_TIER_BBOX)
                : selectedMunicipalities.length > 0
                  ? null
                  : (!searchAllNorway && mapBounds ? mapBounds : null),
      municipality_numbers: selectedMunicipalities.length > 0 ? selectedMunicipalities : undefined,
      country: selectedCountry,
      use_case: useCase,
      // DC site-size override (audit 2026-05-28). Sent only when
      // the user has typed a custom value. Backend computes the
      // same `operational` tier (~0.20 ha/MW with floor) when
      // absent, optionally adjusted by cooling_strategy below.
      ...(useCase === 'data_center'
          && siteHaOverride != null
          ? { required_ha: siteHaOverride } : {}),
      // Ammonia: whole-plant footprint, not the electrolyser alone.
      ...(isAmmonia ? { required_ha: calcAmmoniaPlantArea(powerCapacityMw).totalHa } : {}),
      // Pass the cooling strategy so the backend's site-size tier
      // calc can apply the same ×0.7 multiplier as the frontend
      // when liquid cooling is selected.
      ...(useCase === 'data_center' && coolingStrategy
          ? { cooling_strategy: coolingStrategy } : {}),
      // Always live engine — no synthesized pool. Free-tier inherits
      // the same Kartverket WFS dependency as premium. When WFS is
      // flaky the search fails; that's preferred to surfacing fake
      // parcel data that the user can't trust.
      // Capacity-first chain: passes the top-N substations from
      // /confirmed-capacity directly so the backend skips its own
      // Phase 1 bbox query on data_layers.grid_connections.
      ...(seedSubstations && seedSubstations.length > 0
          ? { seed_substations: seedSubstations } : {}),
    });

    try {
      // Attach the Auth0 bearer if the user is signed in. PR-0 made
      // /sites/search auth-required; without this header it 401s.
      const headers = { 'Content-Type': 'application/json' };
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();
          if (token) headers.Authorization = `Bearer ${token}`;
        } catch (tokErr) {
          // Token refresh failed (Auth0 session expired or popup blocked).
          // Let the request go out anonymous so the server's 401 surfaces
          // the same error the user would see otherwise — no silent
          // half-state.
          console.warn('[SiteFinder] getAccessTokenSilently failed:', tokErr);
        }
      }
      const resp = await fetch(`${apiBase}/api/v1/sites/search`, {
        method: 'POST',
        headers,
        body,
        signal: controller.signal,
      });

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n\n');
        buf = lines.pop(); // keep incomplete chunk
        for (const chunk of lines) {
          const dataLine = chunk.split('\n').find(l => l.startsWith('data:'));
          if (!dataLine) continue;
          try {
            const event = JSON.parse(dataLine.slice(5).trim());
            const label = STAGE_LABELS[event.stage]?.(event);
            if (label) {
              setProgressLog(prev => {
                // Remove the initial "connecting" placeholder on first real event
                const filtered = prev.filter(l => l.stage !== 'connecting');
                // Replace existing entry for same stage, or append
                const idx = filtered.findIndex(l => l.stage === event.stage);
                const entry = { stage: event.stage, label };
                if (idx >= 0) { const next = [...filtered]; next[idx] = entry; return next; }
                return [...filtered, entry];
              });
            }
            if (event.stage === 'complete') {
              const res = event.results || [];
              // Prefer event.total (backend's explicit count) since
              // event.results may be missing/null in some response
              // shapes. Falls back to len(res) only if total is absent.
              _completeCount = typeof event.total === 'number' ? event.total : res.length;
              // Tier counts (Option 3) — captured for telemetry. Backend
              // sets these in search_meta; fall back to client-side count
              // by result_tier so older backends without the field still
              // emit something usable.
              _tierCounts = event.search_meta?.tier_counts || {
                industrial: res.filter(r => (r.result_tier || 'industrial') === 'industrial').length,
                rezoning:   res.filter(r => r.result_tier === 'rezoning').length,
              };
              if (CAPACITY_FIRST_ENABLED) {
                console.log(`[capacity-first] SSE complete: total=${event.total}, results.length=${res.length}, _completeCount=${_completeCount}`);
              }
              setTeigResults(res);
              setTeigExcluded(event.excluded || []);
              setTeigHardExcluded(event.hard_excluded || []);
              setTeigHardExcludedSummary(event.hard_excluded_summary || {});
              setTeigFunnel(event.funnel || null);
              setSearchMeta(event.search_meta || null);
              if (onTeigResultsChange) onTeigResultsChange(res);
              // Persist results to localStorage so a browser tab
              // discard / reload / close doesn't wipe the user's work.
              // TTL + country/use-case guards cap staleness (see
              // LAST_SEARCH_KEY).
              try {
                _lastSearchStore()?.setItem(LAST_SEARCH_KEY, JSON.stringify({
                  savedAt: Date.now(),
                  country: selectedCountry,
                  useCase,
                  results: res,
                  excluded: event.excluded || [],
                  hardExcluded: event.hard_excluded || [],
                  hardExcludedSummary: event.hard_excluded_summary || {},
                  funnel: event.funnel || null,
                  searchMeta: event.search_meta || null,
                }));
              } catch {}  // quota / private mode / disabled — silently skip
              // Don't auto-pan via onSubstationPan — that would reset analysisResult.
              // Let the user click a card to navigate.
              setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
            }
          } catch {}
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') setError(`${t('siteFinder.searchFailed')}: ${err.message}`);
    } finally {
      if (!holdSearchingFlag) setIsSearching(false);
      abortControllerRef.current = null;
      // Update cursor tooltip with result count, then fade out after 10s
      const count = _completeCount ?? 0;
      const resultText = count === 0 ? 'No sites found' : count === 1 ? '1 site found' : `${count} sites found`;
      setCursorTooltip({ visible: true, text: resultText, fading: false });
      cursorTooltipTimeoutRef.current = setTimeout(() => {
        setCursorTooltip(prev => ({ ...prev, fading: true }));
        setTimeout(() => setCursorTooltip({ visible: false, text: '', fading: false }), 500);
      }, 10000);
    }
    return { count: _completeCount, wfsErrorCount: _wfsErrorCount, tierCounts: _tierCounts };
  };

  // Convert a zone result to a site-compatible object for the analysis panel
  const zoneToSite = (z) => {
    const muniName = KOMMUNE_NAMES[z.municipality] || z.municipality;
    return {
      location:            z.location,
      name:                `${z.zone_name} — ${muniName}`,
      nearest_grid:        { id: z.nearest_grid.id, name: z.nearest_grid.name, voltage_level: z.nearest_grid.voltage_level },
      grid_distance_km:    z.nearest_grid.distance_km,
      voltage_level_kv:    z.nearest_grid.voltage_level,
      zoning_score:        z.zone_suitability,
      zoning_info:         { zone_code: z.zone_code, zone_name: z.zone_name, municipality: muniName, zone_suitability: z.zone_suitability },
      protected_distance_km: 999,
      port_distance_km:    null,
      total_parcel_area_ha: z.area_ha,
      _zone_result:        { ...z, municipality_name: muniName },
      _search_criteria:    {
        maxGridDistance,
        minVoltage,
        minAreaHa: calcAreaRequirement(powerCapacityMw, technology).totalHa,
        minProtectedDistance,
        maxPortDistance,
        minPortDepth,
        maxWaterDistance,
      },
    };
  };

  // ── Helpers ────────────────────────────────────────────────
  const getSiteName = (site) => {
    const name = site.nearest_grid?.name;
    if (name && !name.toLowerCase().includes('unknown') && name !== 'N/A') return name;
    const zone = siteZone(site);
    return zone ? `${zone} Grid Connection` : 'Grid Connection';
  };

  const getSiteLocation = (site) => {
    const municipality = site.zoning_info?.municipality;
    const zone = siteZone(site);
    const zoneLabels = { NO1: 'Southeast', NO2: 'Southwest', NO3: 'Central', NO4: 'North', NO5: 'West' };
    const isMuniName = municipality && !/^\d+$/.test(municipality);
    if (isMuniName && zone) return `${municipality} · ${zone}`;
    if (isMuniName) return municipality;
    if (zone) {
      const lat = site.location?.latitude?.toFixed(1);
      const lon = site.location?.longitude?.toFixed(1);
      return `${zone} (${zoneLabels[zone] || ''}) · ${lat}°N, ${lon}°E`;
    }
    const lat = site.location?.latitude?.toFixed(2);
    const lon = site.location?.longitude?.toFixed(2);
    return lat ? `${lat}°N, ${lon}°E` : null;
  };

  const getWaterInfo = (site) => {
    const { lake_distance_km: lk, river_distance_km: rv, lake_name, river_name, water_distance_km: wk,
            freshwater_distance_km: fw, saltwater_distance_km: sw, water_source_type } = site;
    const waterLabel = water_source_type === 'saltwater' ? 'Sjø' :
                       water_source_type === 'both' ? 'Sjø/elv' : null;
    const waterDist = Math.min(
      fw != null ? fw : Infinity,
      sw != null ? sw : Infinity
    );
    if (waterLabel && isFinite(waterDist)) return `${waterLabel} · ${waterDist.toFixed(1)} km`;
    if (lk != null && (rv == null || lk <= rv)) return `${lake_name || 'Innsjø'} · ${lk.toFixed(1)} km`;
    if (rv != null) return `${river_name || 'Elv'} · ${rv.toFixed(1)} km`;
    if (wk != null) return `Elv/innsjø · ${wk.toFixed(1)} km`;
    return null;
  };

  const voltageLabel = (kv) => kv >= 300 ? 'Gold' : kv >= 132 ? 'Silver' : kv >= 66 ? 'Bronze' : 'Local';

  // Backend already sorts the teig-first SSE results before sending
  // them — hydrogen ranks by LCOH ascending, DC ranks by
  // readiness_score descending (sites.py final-sort block). Any
  // client-side resort here just throws that ordering away, so we
  // trust the backend and render the array as-is.

  // Client-side sort of grid-first results
  const sortedSites = useMemo(() => {
    if (!results?.sites) return [];
    return [...results.sites].sort((a, b) => {
      if (sortMode === 'voltage') return (b.voltage_level_kv ?? 0) - (a.voltage_level_kv ?? 0);
      if (sortMode === 'distance') return (a.grid_distance_km ?? 99) - (b.grid_distance_km ?? 99);
      if (sortMode === 'port') return (a.port_distance_km ?? 999) - (b.port_distance_km ?? 999);
      if (sortMode === 'water') return (a.water_distance_km ?? 999) - (b.water_distance_km ?? 999);
      if (sortMode === 'protected') return (b.protected_distance_km ?? 0) - (a.protected_distance_km ?? 0);
      // default: voltage desc
      return (b.voltage_level_kv ?? 0) - (a.voltage_level_kv ?? 0);
    });
  }, [results, sortMode]);

  // Fingerprint of every user-controlled filter value sent to the
  // backend. Compared against `lastSearchFilterKey` (set when a search
  // fires) to detect dirty filters since the last run.
  const currentFilterKey = JSON.stringify([
    useCase, selectedCountry, powerCapacityMw, technology,
    minVoltage, maxGridDistance, maxWaterDistance, waterAccessRequired,
    maxSlopePct, maxElevationM, maxBuildingsOnSite,
    maxIxpDistanceKm, maxFiberDistanceKm, maxRoadDistanceKm, roadPreset,
    maxPortDistance, hasSeawaterAccess,
    waterSourceTypes, speciesPreset, roadPreset,
    bridgePreset, waterRightsPreset,
    harbourScreenOn, screenTest, screenCapacityKm, screenHarbourKm, screenNeighbourM, screenReportedQueueOnly,
    maxResidents2km, requireHazmatPort, requireIndustrialZoning, priceZones, maxCoastKm, futureZoning,
    resultLimit, searchAllNorway, selectedMunicipalities,
  ]);
  const filtersDirty = teigResults !== null
    && lastSearchFilterKey !== null
    && lastSearchFilterKey !== currentFilterKey;

  // Per-card render extracted (audit 2026-06-01) so the capacity-first
  // SubstationCard children and the legacy parcel-first results list
  // share one source of truth. Body is the same JSX that previously
  // lived inline in the (teigResults || []).map callback — no
  // behaviour change in the legacy path.
  const renderTeigCard = (t, idx) => {
    const priceZone = siteZone(t) || '–';
    // Single source of truth for the substation displayed on each
    // card. When the capacity-first chain ran, the _capacityContext
    // substation (the one with confirmed/inquiry capacity that the
    // search anchored on) is the analyst-relevant one — the
    // per-parcel KNN against the local grid_connections table just
    // returns "whatever row is geographically closest", which can
    // be a generic 66 kV transformer 3 km from a 420 kV substation
    // we actually want them looking at. Previously the card line
    // said "Håvik · 420 kV" (local KNN) while the pill said
    // "Above voltage · 132 → 66 kV step-down" (capacity context),
    // referring to two different physical substations on the same
    // card. Capacity context wins when present.
    const cc = t._capacityContext;
    const kv = cc?.voltage_kv ?? t.nearest_grid_voltage_kv ?? t.src_sub_kv ?? '?';
    const distKm = cc?.distance_km?.toFixed?.(1)
                   ?? t.nearest_grid_distance_km?.toFixed?.(1)
                   ?? '–';
    // Fallback display when the raw name is "Unknown" / "null" /
    // empty — pulls operator + station_id so cards don't read
    // "Unknown · 66 kV · 0.1 km". 2026-06-05.
    const _rawName = cc?.name || t.nearest_grid_name || t.src_sub_name;
    const _useless = !_rawName || /^(unknown|null|none|n\/a)$/i.test(String(_rawName).trim());
    const gridName = (() => {
      if (!_useless) return _rawName;
      const op = cc?.operator || t.nearest_grid_operator;
      const sid = cc?.station_id ? `#${String(cc.station_id).slice(-6)}` : '';
      const parts = [op, kv !== '?' ? `${kv} kV` : '', sid].filter(Boolean);
      return parts.length > 0 ? `Unnamed — ${parts.join(' · ')}` : t('siteFinder.unknownStation');
    })();
    const suit = t.zone_suitability != null ? (t.zone_suitability * 100).toFixed(0) : null;
    const zoneSuitability = t.zone_suitability;
    const zoneBadgeClass = zoneSuitability >= 0.7 ? 'rc-zone-green'
      : zoneSuitability >= 0.4 ? 'rc-zone-amber'
      : zoneSuitability != null ? 'rc-zone-red' : 'rc-zone-grey';
    const muniName = t.municipality_name || t.municipality || '—';
    const hasAnyBlocker = t.flood_zone || t.har_kulturminne || t.is_salmon_river || t.nin_type_a;
    return (
      <div key={t.teig_id || idx} className="result-card"
        onClick={() => onTeigSelect && onTeigSelect(t, powerCapacityMw)}
        style={{ position: 'relative' }}>
        {(() => {
          const alreadySaved = isSaved(t.lat, t.lon);
          const freeCapHit = !isPremium && !alreadySaved && projectCount >= FREE_TIER_PROJECT_CAP;
          const handleClick = (e) => {
            e.stopPropagation();
            if (alreadySaved) {
              const proj = findSavedProject(t.lat, t.lon);
              if (proj) deleteProject(proj.id);
              return;
            }
            if (freeCapHit) {
              // Implementation Brief 2026-06-07: 4th-save on free
              // tier opens Calendly directly with email prefill —
              // replaces the previous PremiumLock modal path.
              openCalendly(auth0User);
              return;
            }
            saveProject({ ...t, country: t.country || selectedCountry, use_case: t.use_case || useCase });
            // Filter snapshot at save time. Paying-customer save behaviour
            // is the second half of the prune-decision conjunction
            // (search usage <5% AND saved-project usage <10%) — a filter
            // can look unused in aggregate but be load-bearing for the
            // serious-user subset that actually saves results.
            emitTelemetry({
              event_type: 'save_project',
              requested_mw: powerCapacityMw,
              total_power_draw_mw: totalPowerDrawMw,
              required_voltage_kv: minVoltage,
              detected_region: t.region || null,
              filters_active: snapshotActiveFilters(),
              // Which tier did the user save from? Combined with the
              // search-event tier_counts this gives us the conversion
              // funnel per tier: "N rezoning candidates shown → M saved".
              // Tells us whether the rezoning section is a real value-add
              // or just visual noise.
              saved_tier: t.result_tier || 'industrial',
            });
          };
          return (
            <button
              onClick={handleClick}
              disabled={isSaving(t.lat, t.lon)}
              title={
                alreadySaved ? 'Saved — click to unsave (frees a project slot)'
                : freeCapHit ? `Free tier: ${FREE_TIER_PROJECT_CAP} of ${FREE_TIER_PROJECT_CAP} projects used — Book a call for unlimited`
                : 'Save as project'
              }
              className={alreadySaved ? 'pp-save-saved-toggle' : undefined}
              style={{
                position: 'absolute', top: 8, right: 8, zIndex: 2,
                padding: '4px 12px', borderRadius: 4,
                fontSize: 11, fontWeight: 600, letterSpacing: 0.02,
                border: alreadySaved ? '1px solid rgba(34,197,94,0.55)' : '1px solid rgba(34,211,238,0.4)',
                cursor: 'pointer',
                background: alreadySaved ? '#22c55e'
                          : freeCapHit ? 'rgba(148,163,184,0.15)'
                          : 'rgba(34,211,238,0.12)',
                color: alreadySaved ? '#0f172a'
                      : freeCapHit ? '#94a3b8'
                      : '#22d3ee',
                minWidth: 56,
                textAlign: 'center',
              }}>
              {alreadySaved ? 'Saved ✕'
                : isSaving(t.lat, t.lon) ? '...'
                : freeCapHit ? 'Save 🔒'
                : 'Save'}
            </button>
          );
        })()}
        {(() => {
          // Screening estimate only. The precise DEM-based number is
          // computed on demand when the user opens the Site Analysis
          // panel and clicks "Compute precise buildable area" — not
          // automatically per result (2026-06-04 revert).
          const netHa     = t.capacity_effective_buildable_ha ?? null;
          const platHa    = t.buildable_contiguous_ha ?? null;
          const totalHa   = t.total_area_ha ?? t.areal_ha ?? null;
          if (netHa == null && platHa == null && totalHa == null) return null;
          const primary = netHa ?? platHa ?? totalHa;
          const label = netHa != null ? 'net buildable'
                      : platHa != null ? 'buildable platform'
                      : null;
          const showTotalSub = totalHa != null && primary !== totalHa
                              && Math.abs(totalHa - primary) > 0.5;
          return (
            <div className="rc-headline">
              <span className="rc-rank" title="Place in this list; the map shows the same number on hover">#{idx + 1}</span>{' '}
              {primary.toFixed(1)} ha
              {label && (<span className="rc-headline-sub"> {label}</span>)}
              {showTotalSub && (
                <span className="rc-headline-sub">
                  {' '}· of {totalHa.toFixed(0)} ha parcel
                </span>
              )}
              {t.constituent_count > 1 && (
                <span className="rc-headline-sub"> · {t.constituent_count} parcels</span>
              )}
            </div>
          );
        })()}
        {gridName && (
          <div className="rc-meta-row rc-meta-grid">
            {[gridName, kv !== '?' ? `${kv} kV` : null, distKm !== '–' ? `${distKm} km` : null]
              .filter(Boolean).join(' · ')}
          </div>
        )}
        {t.harbour_screen && (() => {
          // Facts from the harbour + capacity screen: the grid point whose
          // published capacity passed, the harbour, and the nearest home.
          const s = t.harbour_screen;
          const gp = s.grid_point || {};
          const hb = s.harbour || {};
          const q = gp.queue_mw == null ? '?' : `${gp.queue_mw} MW`;
          const lbl = { color: '#64748b', fontWeight: 600, marginRight: 4 };
          return (
            <div className="rc-meta-row" style={{ fontSize: 11, lineHeight: 1.5, color: '#cbd5e1', display: 'block' }}>
              <div><span style={lbl}>Grid</span>{gp.name}{gp.owner ? ` (${gp.owner})` : ''} · {gp.distance_km} km · {gp.available_mw} MW free · queue {q}{gp.queue_reported === false ? ' (not reported)' : ''}{gp.is_area ? ' · area figure' : ''}{gp.updated_at ? ` · updated ${gp.updated_at}` : gp.data_date ? ` · as of ${gp.data_date}` : ''}</div>
              <div><span style={lbl}>Harbour</span>{hb.name}{hb.port && hb.port !== hb.name ? ` · ${hb.port}` : ''} · {hb.distance_km} km{hb.calls != null ? ` · ${hb.calls} calls in ${hb.calls_year || 'last year'}` : ''}</div>
              <div><span style={lbl}>Homes</span>{(h => (s.nearest_home_m == null ? 'none within 5 km' : h.atLimit ? `${h.text.toLowerCase()} away` : `nearest ${h.text}`))(nearestHomeText(s.nearest_home_m, s.limits?.min_neighbour_m))} · {s.qualifying_ha} ha of the {s.parcel_area_ha} ha parcel meets every rule</div>
              {Array.isArray(t.screen_tests_passed) && t.screen_tests_passed.length > 0 && (
                <div><span style={lbl}>Passes</span>{t.screen_tests_passed.join(' · ')} · ranked by the siting brief</div>
              )}
            </div>
          );
        })()}
        {/* Capacity pill — present only when the capacity-first chain
            ran and attached _capacityContext. Shows the binding grid
            constraint per site so the analyst doesn't have to map
            from the substation summary back to each parcel. */}
        {t._capacityContext && (() => {
          const cc = t._capacityContext;
          const pill = {
            sufficient: {
              bg: 'rgba(34,197,94,0.10)', border: '#22c55e', fg: '#22c55e',
              text: `Capacity OK · ${cc.available_cons_mw ?? '?'} MW available`,
            },
            partial: {
              bg: 'rgba(245,158,11,0.10)', border: '#f59e0b', fg: '#fbbf24',
              text: `Partial capacity · ${cc.available_cons_mw ?? '?'} MW available`,
            },
            capacity_inquiry: {
              bg: 'rgba(59,130,246,0.10)', border: '#3b82f6', fg: '#60a5fa',
              text: 'Capacity inquiry needed',
            },
            voltage_overkill: {
              bg: 'rgba(167,139,250,0.10)', border: '#a78bfa', fg: '#c4b5fd',
              text: `Above voltage · ${cc.voltage_kv} → ${cc.required_voltage_kv} kV step-down`,
            },
            voltage_upgrade: {
              bg: 'rgba(148,163,184,0.10)', border: '#94a3b8', fg: '#cbd5e1',
              text: `Below voltage · ${cc.voltage_kv} → ${cc.required_voltage_kv} kV upgrade`,
            },
            saturated: {
              bg: 'rgba(239,68,68,0.10)', border: '#ef4444', fg: '#f87171',
              text: 'Saturated — no available capacity',
            },
          }[cc.tier_classification] || null;
          if (!pill) return null;
          return (
            <div style={{
              marginTop: 4,
              display: 'inline-block',
              padding: '2px 8px', borderRadius: 12,
              background: pill.bg, border: `1px solid ${pill.border}`,
              fontSize: 11, fontWeight: 600, color: pill.fg,
            }}>
              {pill.text}
            </div>
          );
        })()}
        {(t.urban_distance_km != null || t.slope_pct != null) && (
          <div className="rc-meta-row">
            {[
              t.slope_pct != null ? `${t.slope_pct.toFixed(0)}% slope` : null,
              t.urban_distance_km != null ? `${t.urban_distance_km.toFixed(0)} km to settlement` : null,
              t.building_count != null && t.building_count > 5 ? `${t.building_count} bldgs` : null,
              t.red_listed_count > 0 ? `${t.red_listed_count} red-listed sp.` : null,
            ].filter(Boolean).join(' · ')}
          </div>
        )}
        {(() => {
          const oz = overrideZone(t.zone_code, t.zone_name);
          const zoneLabel = oz.code ? (oz.name || String(oz.code))
                          : t.ar5_name || null;
          const muni = muniName && muniName !== '—' ? muniName : null;
          if (!zoneLabel && !muni) return null;
          return (
            <div className="rc-meta-row rc-meta-zone">
              {[zoneLabel, muni].filter(Boolean).join(' · ')}
            </div>
          );
        })()}
        {(() => {
          const chips = [];
          // Zoning status from the plan layers read separately (2026-10-17)
          if (t.zoning_status === 'current' && t.plan_conflict) chips.push({ k: 'zs', cls: 'rc-flag-warn', label: 'Zoned · plan conflict' });
          else if (t.zoning_status === 'future_A') chips.push({ k: 'zs', cls: 'rc-flag-warn', label: 'Earmarked in master plan (A)' });
          else if (t.zoning_status === 'future_B') chips.push({ k: 'zs', cls: 'rc-flag-warn', label: 'Plan proposal (B)' });
          else if (t.zoning_status === 'not_allowed') chips.push({ k: 'zs', cls: 'rc-flag-bad', label: 'Zoning does not allow' });
          else if (t.zoning_status === 'unknown') chips.push({ k: 'zs', cls: 'rc-flag-warn', label: 'Zoning unknown' });
          if (Array.isArray(t.planning_started) && t.planning_started.length) chips.push({ k: 'ps', cls: 'rc-flag-warn', label: 'Planning started' });
          if (t.water_distance_flag) chips.push({ k: 'water', cls: 'rc-flag-warn', label: 'Water source far' });
          if (t.flood_zone) chips.push({ k: 'flood', cls: 'rc-flag-bad', label: 'Flood zone' });
          if (t.har_kulturminne) chips.push({ k: 'her', cls: 'rc-flag-warn', label: 'Heritage' });
          if (t.is_salmon_river) chips.push({ k: 'salmon', cls: 'rc-flag-bad', label: 'Salmon river' });
          if (t.nin_type_a) chips.push({ k: 'nin', cls: 'rc-flag-bad', label: 'NiN A' });
          if (t.elevation_m != null && t.elevation_m > 600) {
            chips.push({ k: 'elev', cls: 'rc-flag-warn', label: `${Math.round(t.elevation_m)} m a.s.l.` });
          }
          // Concern chips: only render the small structured-label
          // types (slope, ixp). The long-text fall-through (Protected
          // area X km away — EIA consultation likely, Flood caution
          // map clips parcel corner, …) was removed 2026-06-08 per
          // user request — those full sentences belong in the Site
          // Analysis panel under the matching data row, not as
          // yellow chips on every card.
          for (const c of (t.concerns || [])) {
            const cls = c.severity === 'high' ? 'rc-flag-bad' : 'rc-flag-warn';
            let label = null;
            if (c.type === 'slope_unbuildable') label = `${c.message.split(' — ')[0]} cliff`;
            else if (c.type === 'slope_steep')     label = c.message.split(' — ')[0];
            else if (c.type === 'ixp_unknown')     label = 'IXP unknown';
            else if (c.type === 'ixp_far')         label = `IXP ${c.message.match(/(\d+)\s*km/)?.[1] || '?'} km`;
            if (label) chips.push({ k: `c-${c.type}`, cls, label, title: c.message });
          }
          if (chips.length === 0) return null;
          return (
            <div className="rc-flags-row">
              {chips.map(c => (
                <span key={c.k} className={`rc-flag-chip ${c.cls}`} title={c.title || ''}>
                  {c.label}
                </span>
              ))}
            </div>
          );
        })()}
        {(() => {
          // The records behind this card: the WattApp station, the property
          // in Se eiendom, the place on Norgeskart (utils/sourceLinks).
          const links = siteRecordLinks(t);
          if (!links.length) return null;
          return (
            <div className="rc-sources" onClick={e => e.stopPropagation()}>
              <span className="rc-sources-label">Sources</span>
              {links.map(l => (
                <a key={l.short} href={l.url} target="_blank" rel="noopener noreferrer" title={l.label}>{l.short} ↗</a>
              ))}
            </div>
          );
        })()}
      </div>
    );
  };

  // Enrich each SSE parcel with its nearest assessed substation +
  // tier_classification. Renders as a capacity pill on the parcel
  // card so the user sees, per site, whether the grid can power it
  // (Sufficient/Partial), needs an inquiry, needs a voltage upgrade,
  // or is saturated. Uses ALL assessed substations (not just
  // sufficient) — the point of the pill is to flag insufficient grid
  // on land that's otherwise good.
  const parcelsWithCapacity = useMemo(() => {
    if (!teigResults) return teigResults;
    const subs = capacityFirstResponse?.substations || [];
    if (subs.length === 0) return teigResults;
    return teigResults.map(p => {
      if (p.lat == null || p.lon == null) return p;
      let bestSub = null;
      let bestKm = Infinity;
      for (const s of subs) {
        const km = _haversineKm(p.lat, p.lon, s.lat, s.lng);
        if (km < bestKm) { bestKm = km; bestSub = s; }
      }
      if (!bestSub) return p;
      return {
        ...p,
        _capacityContext: {
          station_id:          bestSub.station_id,
          name:                bestSub.name,
          operator:            bestSub.operator,
          voltage_kv:          bestSub.voltage_kv,
          distance_km:         Math.round(bestKm * 10) / 10,
          tier_classification: bestSub.tier_classification,
          capacity_status:     bestSub.capacity_status,
          available_cons_mw:   bestSub.available_cons_mw,
          required_voltage_kv: bestSub.required_voltage_kv,
          meets_required_voltage: bestSub.meets_required_voltage,
        },
      };
    });
  }, [capacityFirstResponse, teigResults]);

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className={`site-finder-panel ${isOpen ? 'open' : 'closed'}`}>
      {/* Toggle tab on left edge */}
      <button className="sidebar-toggle-tab" onClick={onToggle} title={isOpen ? t('common.close') : t('siteFinder.searchButton')}>
        {isOpen ? (
          <span className="toggle-chevron">›</span>
        ) : (
          <>
            <span className="toggle-chevron">‹</span>
            <span className="toggle-text-vertical">{t('siteFinder.searchButton')}</span>
          </>
        )}
      </button>

      {isOpen && (
        <>
          <div className="finder-header">
            <h2>{t('siteFinder.title')}</h2>
          </div>

          {/* No use-case tabs or country row since 2026-10-14: both are the
              tenant's (utils/tenantScope.js). */}

      <div className="finder-content">

        {/* ── Search Area ─────────────────────────────────── */}
        {/* Hidden entirely for non-premium users — the floating "Free
            tier" banner over the map is the sole scope indicator. The
            all-Norway/visible-region toggle and the municipality
            pickers are premium-only. */}
        {isPremium && (
        <div className="finder-section">
          <div className="section-header" onClick={() => {}}>
            <span className="section-title">{t('siteFinder.searchArea')}</span>
          </div>
          <div className="section-body-always">
            <div className="scope-toggle-container">
              <button
                className={`scope-toggle-btn ${searchAllNorway ? 'active' : ''}`}
                onClick={() => setSearchAllNorway(true)}
              >
                <span className="toggle-text">
                  <span className="toggle-label">{selectedCountry === 'FI' ? 'All Finland' : selectedCountry === 'IT' ? 'All Italy' : t('siteFinder.allNorway')}</span>
                  <span className="toggle-subtitle">{selectedCountry === 'FI' ? 'Search entire Finland' : selectedCountry === 'IT' ? 'Search entire Italy' : 'Search entire Norway'}</span>
                </span>
              </button>
              <button
                className={`scope-toggle-btn ${!searchAllNorway ? 'active' : ''}`}
                onClick={() => setSearchAllNorway(false)}
              >
                <span className="toggle-text">
                  <span className="toggle-label">{t('siteFinder.visibleRegion')}</span>
                  <span className="toggle-subtitle">{t('siteFinder.currentMapView')}</span>
                </span>
              </button>
            </div>
            {selectedMunicipalities.length > 0 ? (
              <p className="scope-info">Searching only the selected municipalities.</p>
            ) : !searchAllNorway && mapBounds && (
              <p className="scope-info">{t('siteFinder.regionSelected')}</p>
            )}
            {!searchAllNorway && !mapBounds && (
              <p className="scope-warning">{t('siteFinder.regionWarning')}</p>
            )}
            {/* Finnish municipality picker */}
            {selectedCountry === 'FI' && (
              <div className="muni-picker">
                <div className="muni-picker-header">
                  <span className="muni-picker-label">Kunta (municipality)</span>
                  {selectedMunicipalities.length > 0 && (
                    <span className="muni-selected-count">{selectedMunicipalities.length} selected</span>
                  )}
                </div>
                <input
                  className="muni-search-input"
                  type="text"
                  placeholder="Search Finnish municipality…"
                  value={muniSearch}
                  onChange={e => setMuniSearch(e.target.value)}
                />
                {selectedMunicipalities.length > 0 && (
                  <div className="muni-chips">
                    {selectedMunicipalities.map(code => (
                      <span key={code} className="muni-chip"
                        onClick={() => setSelectedMunicipalities(prev => prev.filter(c => c !== code))}>
                        {FI_KUNTA_NAMES[code] || code} ✕
                      </span>
                    ))}
                  </div>
                )}
                <div className="muni-list">
                  {FI_KUNNAT.filter(m => {
                    const q = muniSearch.toLowerCase();
                    return !q || m.code.includes(q) || m.name.toLowerCase().includes(q);
                  }).slice(0, 12).map(m => (
                    <label key={m.code} className="muni-list-item">
                      <input type="checkbox"
                        checked={selectedMunicipalities.includes(m.code)}
                        onChange={e => {
                          if (e.target.checked) setSelectedMunicipalities(prev => [...prev, m.code]);
                          else setSelectedMunicipalities(prev => prev.filter(c => c !== m.code));
                        }}
                      />
                      <span className="muni-name">{m.name}</span>
                      <span className="muni-count">{m.region}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {searchMode === 'zone' && selectedCountry !== 'FI' && (
              <div className="muni-picker">
                <div className="muni-picker-header">
                  <span className="muni-picker-label">{t('siteScreener.municipalities')}</span>
                  {selectedMunicipalities.length > 0 && (
                    <span className="muni-selected-count">{selectedMunicipalities.length} {t('common.select')}</span>
                  )}
                </div>
                <input
                  className="muni-search-input"
                  type="text"
                  placeholder={t('siteFinder.searchPlaceholder')}
                  value={muniSearch}
                  onChange={e => setMuniSearch(e.target.value)}
                />
                {selectedMunicipalities.length > 0 && (
                  <div className="muni-chips">
                    {selectedMunicipalities.map(code => {
                      const m = municipalityList.find(m => m.code === code);
                      return (
                        <span key={code} className="muni-chip" onClick={() =>
                          setSelectedMunicipalities(prev => prev.filter(c => c !== code))
                        }>
                          {m?.name || code} ✕
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="muni-list">
                  {(() => {
                    const q = muniSearch.toLowerCase();
                    const filtered = municipalityList.filter(m => {
                      const names = [m.name, m.name_sami].filter(Boolean).join(' ').toLowerCase();
                      return !q || m.code.includes(q) || names.includes(q);
                    });
                    // Norway lists all 357 municipalities, alphabetically.
                    // Italy has ~7,900 comuni, so its unfiltered list stays
                    // capped; once the user searches, every match shows.
                    return (q || selectedCountry !== 'IT') ? filtered : filtered.slice(0, 50);
                  })().map(m => (
                      <label key={m.code} className="muni-list-item">
                        <input
                          type="checkbox"
                          checked={selectedMunicipalities.includes(m.code)}
                          onChange={e => {
                            if (e.target.checked) setSelectedMunicipalities(prev => [...prev, m.code]);
                            else setSelectedMunicipalities(prev => prev.filter(c => c !== m.code));
                          }}
                        />
                        <span className="muni-name">{m.name || m.code}</span>
                        <span className="muni-count">{m.county || m.region || ''}</span>
                      </label>
                    ))
                  }
                  {municipalityList.length === 0 && (
                    <p className="muni-empty">{t('siteFinder.loading')}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        )}

        {/* ── Project Parameters ──────────────────────────── */}
        <div className="finder-section">
          <div className="section-header" onClick={() => setShowProjectParams(!showProjectParams)}>
            <span className="section-title">{t('siteFinder.project')}</span>
            <span style={{ fontSize: 11, color: '#7c829e', marginRight: 4 }}>
              {useCase === 'data_center' ? `${powerCapacityMw} MW IT` : `${technology} · ${powerCapacityMw} MW`}
            </span>
            <span className={`section-chevron ${showProjectParams ? 'open' : ''}`}>▼</span>
          </div>

          {showProjectParams && (
            <div className="section-body">
              {/* Technology — hydrogen only */}
              {useCase !== 'data_center' && <div className="filter-control">
                <label><span>{t('siteFinder.technology')}</span></label>
                <div className="tech-toggle">
                  <button
                    className={`tech-btn ${technology === 'PEM' ? 'active' : ''}`}
                    onClick={() => setTechnology('PEM')}
                  >
                    PEM
                    <span className="tech-hint">230 m²/MW</span>
                  </button>
                  <button
                    className={`tech-btn ${technology === 'AWE' ? 'active' : ''}`}
                    onClick={() => setTechnology('AWE')}
                  >
                    AWE
                    <span className="tech-hint">300 m²/MW</span>
                  </button>
                </div>
              </div>}

              {/* MW slider (full width — Buffer control was removed; the
                  500 m default is hardcoded backend-side and the picker
                  wasn't meaningfully changing results). */}
              <div className="filter-control">
                <label>
                  <span>{useCase === 'data_center' ? 'IT Load' : t('siteFinder.capacity')}</span>
                  <span className="filter-value">{powerCapacityMw} MW</span>
                </label>
                {/* Two-segment scale: 0-100 internal → 1-100 MW, 100-200 internal → 100-500 MW.
                    This puts 100 MW at exactly the midpoint of the slider track. */}
                <input
                  type="range" min="0" max="200" step="1"
                  value={powerCapacityMw <= 100 ? (powerCapacityMw - 1) : 100 + (powerCapacityMw - 100) / 4}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setPowerCapacityMw(v <= 100 ? Math.round(1 + v) : Math.round(100 + (v - 100) * 4));
                  }}
                  className="filter-slider"
                />
                <div className="mw-markers">
                  <span>1</span>
                  <span>100</span>
                  <span>500</span>
                </div>
              </div>

              {/* Seawater — hydrogen only. The ammonia plant model below shows
                  fresh process water and seawater cooling itself; the
                  toggle changed nothing there. */}
              {useCase !== 'data_center' && !isAmmonia && (
                <div className="filter-control checkbox-control">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={hasSeawaterAccess}
                      onChange={(e) => setHasSeawaterAccess(e.target.checked)}
                    />
                    <span>{t('siteFinder.seawaterAccess')}</span>
                    <span className="filter-hint">{hasSeawaterAccess ? '200 l/hr/MW' : '800 l/hr/MW'}</span>
                  </label>
                </div>
              )}

              {/* Live assessment preview */}
              {useCase === 'data_center' ? (() => {
                // Audit 2026-05-28: site-size tiers replaced the old
                // 0.4/0.6/1.0 ha/MW heuristic with a non-linear model
                // anchored to real-world benchmarks. See backend
                // calculate_site_size_tiers — building/operational/
                // expansion with a small-site floor for fixed-cost
                // overheads (substation pad, perimeter, access road).
                // Cooling-strategy multiplier (×0.7 for liquid) applies
                // to the operational tier only.
                const coolingMult = coolingStrategy === 'liquid' ? 0.7 : 1.0;
                const buildingHa    = Math.round(Math.max(powerCapacityMw * 0.10, 1.5) * 10) / 10;
                // 0.15 ha/MW for operational tier (0.20→0.18→0.15). Bottom
                // of the plausible-range for free-cooling DX in Nordic
                // climate; 0.13 explicitly declined as below-range for
                // the typical user profile. MUST match backend
                // _SITE_SIZE_RATIO['operational'] in site_capacity.py.
                const recommendedHa = Math.round(Math.max(powerCapacityMw * 0.15 * coolingMult, 3.0) * 10) / 10;
                const expansionHa   = Math.round(Math.max(powerCapacityMw * 0.35, 5.0) * 10) / 10;
                // User override wins; null means "use recommended"
                // (auto-tracks MW + cooling via the useEffect above).
                const siteHa        = siteHaOverride != null ? siteHaOverride : recommendedHa;
                const isOverride    = siteHaOverride != null && siteHaOverride !== recommendedHa;
                const totalDraw  = Math.round(powerCapacityMw * dcPue);
                // Voltage tier is now read-only, auto-derived from MW.
                // No user override — search prioritizes right-sized substations.
                return (
                  <div className="project-assessment-preview dc-assess-v2">
                    <div className="dc-input-row">
                      <span className="dc-label">IT load</span>
                      <span className="dc-value dc-value-strong"><strong>{powerCapacityMw} MW</strong></span>
                    </div>
                    <div className="dc-input-row">
                      <span className="dc-label">Cooling strategy</span>
                      <span className="dc-input-cell">
                        <select
                          className="cooling-strategy-select"
                          value={coolingStrategy}
                          onChange={(e) => setCoolingStrategy(e.target.value)}
                        >
                          {COOLING_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>
                              {o.label} (PUE {o.pue.toFixed(2)})
                            </option>
                          ))}
                        </select>
                        <span className="dc-info-tip" title="Free cooling DX achievable in Norway's climate (PUE 1.08–1.20 typical for 200+ free-cool days/yr)">(?)</span>
                      </span>
                    </div>

                    <div className="dc-section-divider">Calculated requirements</div>

                    {/* Site size — editable. Auto-derives from MW × 0.6
                        until the user types a value; the useEffect above
                        clears any override whenever MW changes so the
                        field always reflects the current project. */}
                    <div className="dc-output-row">
                      <span className="dc-label">Site size</span>
                      <span className="dc-input-cell">
                        <input
                          type="number"
                          min="1"
                          step="1"
                          className="dc-ha-input"
                          value={siteHa}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            setSiteHaOverride(Number.isFinite(v) && v > 0 ? v : null);
                          }}
                        />
                        <span className="dc-ha-suffix">ha</span>
                        {isOverride && (
                          <button type="button"
                                  className="dc-reset-btn"
                                  onClick={() => setSiteHaOverride(null)}
                                  title="Revert to default">reset</button>
                        )}
                      </span>
                    </div>
                    <div className="dc-output-row">
                      <span className="dc-label">Power draw</span>
                      <span className="dc-value dc-value-strong"><strong>{totalDraw} MW</strong></span>
                    </div>
                    <div className="dc-output-row">
                      <span className="dc-label">Grid voltage</span>
                      <span className="dc-value">
                        ≥ {suggestedVoltage} kV
                        <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 6 }}>(auto from MW)</span>
                      </span>
                    </div>

                    <details className="dc-details-drawer">
                      <summary>Site size breakdown</summary>
                      <div className="dc-detail-content">
                        <div className="dc-detail-row">
                          <span className="dc-detail-label">Building footprint</span>
                          <span className="dc-detail-value">{buildingHa} ha</span>
                        </div>
                        <div className="dc-detail-note">
                          Data halls + immediate equipment yard
                        </div>
                        <div className="dc-detail-row" style={{ marginTop: 8 }}>
                          <span className="dc-detail-label">Operational site</span>
                          <span className="dc-detail-value">{recommendedHa} ha</span>
                        </div>
                        <div className="dc-detail-note">
                          + cooling, substation, parking, setbacks (used as filter threshold)
                        </div>
                        <div className="dc-detail-row" style={{ marginTop: 8 }}>
                          <span className="dc-detail-label">With expansion buffer</span>
                          <span className="dc-detail-value">{expansionHa} ha</span>
                        </div>
                        <div className="dc-detail-note">
                          + room to roughly double capacity (phase 2 reserve)
                        </div>
                        <div className="dc-detail-note" style={{ marginTop: 10 }}>
                          Based on 0.10 / 0.20 / 0.35 ha per MW IT with small-site floors.
                          References — Meta Lulea, Google Hamina, Bulk N01, Cushman & Wakefield.
                          {coolingStrategy === 'liquid' && ' Liquid cooling × 0.7 applied to operational tier.'}
                        </div>
                        <div className="dc-detail-row" style={{ marginTop: 10 }}>
                          <span className="dc-detail-label">Power draw breakdown</span>
                          <span className="dc-detail-value">{powerCapacityMw} × {dcPue.toFixed(2)} = {totalDraw} MW</span>
                        </div>
                        <div className="dc-detail-note">
                          PUE × IT load = total facility draw including cooling, lighting, losses.
                        </div>
                        <div className="dc-detail-row" style={{ marginTop: 10 }}>
                          <span className="dc-detail-label">Voltage requirement</span>
                          <span className="dc-detail-value">{powerCapacityMw > 20 ? '132 kV @ ≥20 MW' : '66 kV @ <20 MW'}</span>
                        </div>
                        <div className="dc-detail-note">
                          Single-feed transformer capacity at typical Norwegian regional voltages.
                        </div>
                      </div>
                    </details>
                  </div>
                );
              })() : (() => {
                const elecArea = calcAreaRequirement(powerCapacityMw, technology);
                const area = isAmmonia ? calcAmmoniaPlantArea(powerCapacityMw) : elecArea;
                const water = calcWaterNeeds(powerCapacityMw, hasSeawaterAccess);
                const nh3 = isAmmonia ? calcAmmoniaWater(powerCapacityMw) : null;
                const reqKv = requiredVoltage(powerCapacityMw);
                return (
                  <div className="project-assessment-preview">
                    <div className="assess-row">
                      <span className="assess-label">{t('siteFinder.areaRequired')}</span>
                      <span className="assess-value">
                        ~{area.totalHa} ha
                        <span className="assess-sub">{isAmmonia ? `${area.m2PerMw} m²/MW whole plant · electrolyser alone ${elecArea.totalHa} ha` : `${area.m2PerMw} m²/MW`}</span>
                      </span>
                    </div>
                    {totalSelectedHa > 0 && (
                      <div className="parcel-area-feedback">
                        <span className={totalSelectedHa >= area.totalHa ? 'area-met' : 'area-short'}>
                          {totalSelectedHa.toFixed(2)} ha {t('common.select')}
                        </span>
                        <span className="feedback-muted"> / {area.totalHa} ha {t('siteFinder.necessary')}</span>
                      </div>
                    )}
                    {nh3 ? (<>
                      <div className="assess-row">
                        <span className="assess-label">Process water (fresh)</span>
                        <span className="assess-value">
                          ~{nh3.processM3h} m³/h
                          <span className="assess-sub">≈ 2 m³ per t NH₃ · {nh3.nh3TonnesPerDay} t/day</span>
                        </span>
                      </div>
                      <div className="assess-row">
                        <span className="assess-label">Cooling water</span>
                        <span className="assess-value">
                          ~{nh3.coolingSeaM3h.toLocaleString()} m³/h
                          <span className="assess-sub">once-through seawater · or ~{nh3.towerMakeupM3h} m³/h tower make-up</span>
                        </span>
                      </div>
                      <div className="assess-row">
                        <span className="assess-label">Storage · Seveso</span>
                        <span className="assess-value">
                          ~{nh3.monthlyParcelT.toLocaleString()} t tank
                          <span className="assess-sub">monthly ship parcel · {nh3.monthlyParcelT >= 200 ? 'Seveso upper tier (200 t or more)' : nh3.monthlyParcelT >= 50 ? 'Seveso lower tier (50–200 t)' : 'below the Seveso threshold (50 t)'}</span>
                        </span>
                      </div>
                    </>) : (
                    <div className="assess-row">
                      <span className="assess-label">{t('siteFinder.water')}</span>
                      <span className="assess-value">
                        {water.totalM3hr} m³/hr
                        <span className="assess-sub">{water.lhrPerMw} l/hr/MW · {water.totalM3day} m³/day</span>
                      </span>
                    </div>
                    )}
                    <div className="assess-row">
                      <span className="assess-label">{t('siteFinder.requiredVoltage')}</span>
                      <span className="assess-value">≥ {reqKv} kV</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* ── Hard Filters ────────────────────────────────── */}
        <div className="finder-section">
          <div className="section-header" onClick={() => setShowFilters(!showFilters)}>
            <span className="section-title">{t('siteFinder.filters')}</span>
            <span className={`section-chevron ${showFilters ? 'open' : ''}`}>▼</span>
          </div>

          {showFilters && (
            <div className="section-body">
              {/* ── GROUP: Harbour + published grid capacity screen (2026-10-07) ── */}
              {selectedCountry === 'NO' && useCase === 'hydrogen' && (
              <div className="filter-group">
                <div className="filter-group-header">
                  <h4>Harbour + grid capacity screen</h4>
                  <span className="filter-group-source">WattApp · Kystverket · Kartverket</span>
                </div>
                <div className="filter-group-body" style={{ paddingTop: '8px' }}>
                  <div className="filter-control checkbox-control">
                    <label className="checkbox-label">
                      <input type="checkbox" checked={harbourScreenOn} onChange={(e) => setHarbourScreenOn(e.target.checked)} />
                      <span>Start from published grid capacity</span>
                      <span className="filter-hint">returns parcels clipped to the land that meets every rule below</span>
                    </label>
                  </div>
                  {harbourScreenOn && (<>
                    <div className="filter-control">
                      <label>
                        <span>Capacity test</span>
                        <span className="filter-value">{SCREEN_TESTS[screenTest].hint}</span>
                      </label>
                      <div className="quick-toggle-row">
                        {Object.entries(SCREEN_TESTS).map(([k, v]) => (
                          <button key={k} className={`tech-btn${screenTest === k ? ' active' : ''}`} onClick={() => setScreenTest(k)}>{v.label}</button>
                        ))}
                      </div>
                    </div>
                    <div className="filter-control">
                      <label>
                        <span>Grid point with that capacity within</span>
                        <span className="filter-value">≤ {screenCapacityKm} km</span>
                      </label>
                      <div className="quick-toggle-row">
                        {[5, 10, 20].map(v => (
                          <button key={v} className={`tech-btn${screenCapacityKm === v ? ' active' : ''}`} onClick={() => setScreenCapacityKm(v)}>≤{v} km</button>
                        ))}
                      </div>
                    </div>
                    <div className="filter-control">
                      <label>
                        <span>Harbour, port facility or quay within</span>
                        <span className="filter-value">≤ {screenHarbourKm} km</span>
                      </label>
                      <div className="quick-toggle-row">
                        {[0.5, 1, 2].map(v => (
                          <button key={v} className={`tech-btn${screenHarbourKm === v ? ' active' : ''}`} onClick={() => setScreenHarbourKm(v)}>≤{v} km</button>
                        ))}
                      </div>
                    </div>
                    <div className="filter-control">
                      <label>
                        <span>No home or holiday home within</span>
                        <span className="filter-value">{screenNeighbourM} m</span>
                      </label>
                      <div className="quick-toggle-row">
                        {[300, 500, 1000].map(v => (
                          <button key={v} className={`tech-btn${screenNeighbourM === v ? ' active' : ''}`} onClick={() => setScreenNeighbourM(v)}>{v} m</button>
                        ))}
                      </div>
                    </div>
                    <div className="filter-control checkbox-control">
                      <label className="checkbox-label">
                        <input type="checkbox" checked={screenReportedQueueOnly}
                          disabled={SCREEN_TESTS[screenTest].max_queue_mw == null}
                          onChange={(e) => setScreenReportedQueueOnly(e.target.checked)} />
                        <span>Only grid companies that publish their queue</span>
                        <span className="filter-hint">Lnett and some smaller companies show 0 MW queue everywhere</span>
                      </label>
                    </div>
                    <div className="filter-hint" style={{ padding: '4px 0' }}>
                      Straight-line distances. Capacity is what each grid company publishes; it ignores the regional and transmission grid, and connections of 5 MW or more also join Statnett's queue. While the screen is on, its grid point and harbour rules replace the grid and port distances below.
                    </div>
                  </>)}
                </div>
              </div>
              )}

              {/* ── GROUP: Power / Grid ──────────────────────────────────────── */}
              <div className="filter-group">
                <div className="filter-group-header">
                  <h4>Power / Grid</h4>
                  <span className="filter-group-source">{selectedCountry === 'FI' ? 'Fingrid' : 'NVE Nettanlegg'}</span>
                </div>
                <div className="filter-group-body" style={{ paddingTop: '8px' }}>
                  {screenActive ? (
                    <div className="filter-hint" style={{ padding: '2px 0 6px' }}>
                      Grid distance: set by the screen above (grid point within {screenCapacityKm} km).
                    </div>
                  ) : (
                  <div className="filter-control">
                    <label>
                      <span>{t('siteFinder.maxGridDistance')}</span>
                      <span className="filter-value" style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        ≤{maxGridDistance} km
                        {gridDistanceOverride == null && (
                          <span style={{
                            fontSize: 10, fontWeight: 400,
                            color: 'rgba(255,255,255,0.45)',
                          }}>(suggested)</span>
                        )}
                        {gridDistanceOverride != null
                          && gridDistanceOverride !== suggestedGridDistance && (
                          <button type="button"
                                  onClick={() => setGridDistanceOverride(null)}
                                  title="Revert to suggested"
                                  style={{
                                    fontSize: 10, fontWeight: 500,
                                    color: 'rgba(255,255,255,0.55)',
                                    background: 'transparent',
                                    border: '1px solid rgba(255,255,255,0.18)',
                                    padding: '1px 6px', borderRadius: 3,
                                    cursor: 'pointer',
                                  }}>reset</button>
                        )}
                      </span>
                    </label>
                    <input
                      type="range" min="1" max="100" step="1"
                      value={maxGridDistance}
                      onChange={(e) => setMaxGridDistance(parseFloat(e.target.value))}
                      className="filter-slider"
                    />
                  </div>
                  )}
                  {/* Voltage selector removed 2026-06-01 — minVoltage
                      is now derived from project MW (see useMemo near
                      top). The Calculated Requirements panel surfaces
                      the derived value as "Grid voltage ≥ X kV".
                      Per-substation adequacy badges in capacity-first
                      results show which substations meet that threshold
                      and which need a voltage upgrade. */}
                  {/* Fiber-route distance (NO data_layers.fiber_networks
                      — GlobalConnect dark/lit fiber + carrier backbones).
                      Buckets, not slider — fiber spend has hard cliffs
                      (sub-1 km no dig / 1-5 km feasible / 5-25 km
                      significant / >25 km greenfield from carrier
                      perspective). Default 'Any' = filter off; user opts
                      in. Result panel already displays nearest fiber
                      route, so user can see what they're filtering on. */}
                  {!(isAmmonia && sitingProfile?.filter_groups?.fibre === false) && (
                  <div className="filter-control">
                    <label>
                      <span>Max fiber-route distance</span>
                      <span className="filter-value">
                        {maxFiberDistanceKm == null ? 'Any' : `≤ ${maxFiberDistanceKm} km`}
                      </span>
                    </label>
                    <div className="quick-toggle-row">
                      <button
                        className={`tech-btn${maxFiberDistanceKm === null ? ' active' : ''}`}
                        onClick={() => setMaxFiberDistanceKm(null)}>Any</button>
                      {[1, 5, 10, 25].map(v => (
                        <button key={v}
                          className={`tech-btn${maxFiberDistanceKm === v ? ' active' : ''}`}
                          onClick={() => setMaxFiberDistanceKm(v)}>≤{v} km</button>
                      ))}
                    </div>
                  </div>
                  )}
                </div>
              </div>

              {/* Environment group (protected-area distance slider) removed
                  2026-10-14: the search excludes only parcels that overlap a
                  protected area; nearer ones are flagged on the card. The
                  slider changed nothing. See the "Always excluded" note. */}

              {/* ── GROUP: Port & export (hydrogen only) ─────────────────────
                  Port depth went on 2026-10-14: the search never applied it,
                  and most depths are guessed from the port type. The site
                  card shows the register depth and the ships that call. */}
              {useCase !== 'data_center' && (
              <div className="filter-group">
                <div className="filter-group-header">
                  <h4>Port &amp; export</h4>
                  <span className="filter-group-source">Kystverket</span>
                </div>
                <div className="filter-group-body" style={{ paddingTop: '8px' }}>
                  {screenActive ? (
                    <div className="filter-hint" style={{ padding: '2px 0 6px' }}>
                      Port distance: set by the screen above (harbour, port facility or quay within {screenHarbourKm} km).
                    </div>
                  ) : (
                    <div className="filter-control">
                      <label>
                        <span>{t('siteFinder.portDistance')}</span>
                        <span className="filter-value">≤{maxPortDistance >= 1000 ? `${(maxPortDistance/1000).toFixed(1)} km` : `${maxPortDistance} m`}</span>
                      </label>
                      <input
                        type="range" min="1000" max="100000" step="1000"
                        value={maxPortDistance}
                        onChange={(e) => setMaxPortDistance(parseFloat(e.target.value))}
                        className="filter-slider"
                      />
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* ── GROUP: Water ─────────────────────────────────────────────
                  Hydrogen: distance + type. DC version lives inside the DC
                  block below (it has different labeling + additional fields). */}
              {useCase !== 'data_center' && (
              <div className="filter-group">
                <div className="filter-group-header">
                  <h4>Water</h4>
                  <span className="filter-group-source">{selectedCountry === 'FI' ? 'SYKE / Overpass' : 'NVE Elvenett · Innsjødatabase'}</span>
                </div>
                <div className="filter-group-body" style={{ paddingTop: '8px' }}>
                  <div className="filter-control">
                    <label>
                      <span>{t('siteFinder.waterDistance')}</span>
                      <span className="filter-value">≤{maxWaterDistance} km</span>
                    </label>
                    <input
                      type="range" min="1" max="20" step="1"
                      value={maxWaterDistance}
                      onChange={(e) => setMaxWaterDistance(parseFloat(e.target.value))}
                      className="filter-slider"
                    />
                  </div>
                  <div className="filter-control">
                    <label>
                      <span>Water source type</span>
                      <span className="filter-value">
                        {waterSourceTypes.length === 0 || waterSourceTypes.length === 3
                          ? 'Any' : waterSourceTypes.length + ' selected'}
                      </span>
                    </label>
                    <div className="tech-toggle" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                      {[
                        { v: 'lake',  l: 'Lake' },
                        { v: 'river', l: 'River' },
                        { v: 'sea',   l: 'Sea / Fjord' },
                      ].map(({v, l}) => {
                        const active = waterSourceTypes.includes(v);
                        return (
                          <button key={v} type="button"
                            onClick={() => setWaterSourceTypes(prev =>
                              prev.includes(v)
                                ? prev.filter(x => x !== v)
                                : [...prev, v])}
                            className={`tech-btn ${active ? 'active' : ''}`}>
                            {l}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* Data Center specific filters */}
              {useCase === 'data_center' && <>
                {/* Water-access filter for DC (2026-06-03, revised).
                    DEFAULT: not required. Free-cooling DX make-up water
                    volume is small enough that distance-to-source isn't
                    a screening gate for most projects. User opts in
                    explicitly when their cooling strategy or compliance
                    needs make water proximity a real constraint.
                    Source-type selector (lake/river/sea) still hidden
                    — distance is the actionable part for DC.
                    Hydrogen path treats water as always-required since
                    it's the electrolyser feedstock. */}
                <div className="filter-group">
                  <div className="filter-group-header">
                    <h4>Water</h4>
                    <span className="filter-group-source">NVE Elvenett · Innsjødatabase</span>
                  </div>
                  <div className="filter-group-body" style={{ paddingTop: '8px' }}>
                    <div className="filter-control">
                      <label>
                        <span>Water access</span>
                        <span className="filter-value">
                          {waterAccessRequired ? 'Required' : 'Not required'}
                        </span>
                      </label>
                      <div className="tech-toggle" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                        <button type="button"
                          className={`tech-btn ${!waterAccessRequired ? 'active' : ''}`}
                          onClick={() => setWaterAccessRequired(false)}>
                          Not required
                        </button>
                        <button type="button"
                          className={`tech-btn ${waterAccessRequired ? 'active' : ''}`}
                          onClick={() => setWaterAccessRequired(true)}>
                          Required
                        </button>
                      </div>
                    </div>
                    {waterAccessRequired && (
                      <div className="filter-control" style={{ marginTop: 8 }}>
                        <label>
                          <span>{t('siteFinder.waterDistance')}</span>
                          <span className="filter-value">≤{maxWaterDistance} km</span>
                        </label>
                        <input
                          type="range" min="1" max="20" step="1"
                          value={maxWaterDistance}
                          onChange={(e) => setMaxWaterDistance(parseFloat(e.target.value))}
                          className="filter-slider"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Sidebar cleanup 2026-06-03 — three DC filters cut:
                      • "Max urban distance"        — measured against SSB
                        tettsteder with no population threshold; any 250-person
                        settlement counted the same as Trondheim. Schema needs
                        SSB Statistikkbanken integration to filter by ≥3k pop
                        before this becomes honest. Deferred pending telemetry.
                      • "Min free-cooling days/yr"  — NO data comes from a
                        forecast extrapolation, not actual climate normals;
                        moved to per-site display with verification disclaimer.
                      • "Min distance to residential" — NO Matrikkel residential
                        query doesn't exist; the filter was a placebo for the
                        primary market. Moved to inquiry-workflow guidance.
                    Each removed filter relocates to a more honest place; the
                    criteria are still evaluated, just not via the sidebar.
                    See commit log for context. */}
                {/* Grid redundancy filter removed 2026-06-03 — most NO
                    DC projects accept dual feed where it exists and don't
                    use the filter as a gate. Backend still respects
                    require_dual_feed when set in the body, but the UI no
                    longer surfaces it. */}
              </>}

              {/* ── GROUP: Land & Terrain ─────────────────────────────────── */}
              <div className="filter-group terrain-filter-group">
                <div className="filter-group-header">
                  <h4>Land &amp; Terrain</h4>
                  <span className="filter-group-source">{selectedCountry === 'FI' ? 'MML' : 'Kartverket · Matrikkel'}</span>
                </div>
                <div className="filter-group-body" style={{ paddingTop: '8px' }}>
                  <div className="filter-control">
                    <label>
                      <span>Existing buildings on site</span>
                      <span className="filter-value">
                        {maxBuildingsOnSite === null ? 'any' : maxBuildingsOnSite === 0 ? 'greenfield' : `≤${maxBuildingsOnSite}`}
                      </span>
                    </label>
                    <div className="tech-toggle" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                      {[{v: null, l: 'Any'}, {v: 0, l: 'Greenfield'}, {v: 5, l: '≤5'}, {v: 20, l: '≤20'}].map(({v, l}) => (
                        <button key={l}
                          className={`tech-btn${maxBuildingsOnSite === v ? ' active' : ''}`}
                          onClick={() => setMaxBuildingsOnSite(v)}>{l}</button>
                      ))}
                    </div>
                  </div>
                  {/* Max slope — controls BOTH the screening centroid
                      filter AND the precise DEM compute on final
                      candidates. Sites whose precise buildable area at
                      this slope is below the project's required ha get
                      excluded with a clear reason. Re-added as a visible
                      filter 2026-06-04 (previously hidden at 35% — the
                      precise compute was hardcoded to 15% regardless,
                      causing the screening / precise number divergence
                      the user flagged). */}
                  <div className="filter-control">
                    <label>
                      <span>Max slope for buildable area</span>
                      <span className="filter-value">≤ {maxSlopePct}%</span>
                    </label>
                    <div className="quick-toggle-row">
                      {[10, 15, 20, 25, 30].map(v => (
                        <button key={v}
                          className={`tech-btn${maxSlopePct === v ? ' active' : ''}`}
                          onClick={() => setMaxSlopePct(v)}>≤{v}%</button>
                      ))}
                    </div>
                  </div>
                  <div className="filter-control">
                    <label>
                      <span>{(isAmmonia && sitingProfile?.elevation_label) || t('siteFinder.maxElevation')}</span>
                      <span className="filter-value">{maxElevationM} m a.s.l.</span>
                    </label>
                    <input
                      type="range" min="0" max="1500" step="50"
                      value={maxElevationM}
                      onChange={(e) => setMaxElevationM(parseInt(e.target.value))}
                      className="filter-slider"
                    />
                    <div className="depth-markers"><span>0</span><span>800</span><span>1500</span></div>
                  </div>
                </div>
              </div>

              {/* ── GROUP: Ammonia siting criteria (FFI review, 2026-10-05) ── */}
              {isAmmonia && sitingProfile?.filter_groups?.ammonia_siting !== false && (
              <div className="filter-group">
                <div className="filter-group-header">
                  <h4>Ammonia siting</h4>
                  <span className="filter-group-source">SSB · SafeSeaNet · DIBK · NordPool · Kartverket</span>
                </div>
                <div className="filter-group-body" style={{ paddingTop: '8px' }}>
                  <div className="filter-control">
                    <label>
                      <span>Residents within 2 km (hensynssone)</span>
                      <span className="filter-value">{maxResidents2km == null ? 'Any' : `≤ ${maxResidents2km}`}</span>
                    </label>
                    <div className="quick-toggle-row">
                      <button className={`tech-btn${maxResidents2km === null ? ' active' : ''}`} onClick={() => setMaxResidents2km(null)}>Any</button>
                      {[50, 200, 1000].map(v => (
                        <button key={v} className={`tech-btn${maxResidents2km === v ? ' active' : ''}`} onClick={() => setMaxResidents2km(v)}>≤{v}</button>
                      ))}
                    </div>
                  </div>
                  <div className="filter-control checkbox-control">
                    <label className="checkbox-label">
                      <input type="checkbox" checked={requireHazmatPort} onChange={(e) => setRequireHazmatPort(e.target.checked)} />
                      <span>Harbour already handles gas / tanker calls</span>
                      <span className="filter-hint">SafeSeaNet, last full year</span>
                    </label>
                  </div>
                  <div className="filter-control checkbox-control">
                    <label className="checkbox-label">
                      <input type="checkbox" checked={requireIndustrialZoning} onChange={(e) => setRequireIndustrialZoning(e.target.checked)} />
                      <span>Existing industrial zoning only</span>
                      <span className="filter-hint">kommuneplan næringsformål</span>
                    </label>
                  </div>
                  <div className="filter-control">
                    <label>
                      <span>Include future zoning</span>
                      <span className="filter-value">{{ off: 'Off', A: 'Earmarked (A)', AB: 'Earmarked + proposals (A+B)' }[futureZoning]}</span>
                    </label>
                    <div className="quick-toggle-row">
                      {[['off', 'Off'], ['A', 'Earmarked (A)'], ['AB', '+ proposals (A+B)']].map(([v, l]) => (
                        <button key={v} className={`tech-btn${futureZoning === v ? ' active' : ''}`} onClick={() => setFutureZoning(v)}>{l}</button>
                      ))}
                    </div>
                    <div className="filter-hint" style={{ padding: '4px 0' }}>
                      Off: land an adopted regulatory plan allows a plant on. A: land the master plan designates for næring/industri with no regulatory plan yet. B: a plan proposal at hearing whose land use allows a plant. Read plan by plan from DIBK's plan register; each site shows its plans and status.
                    </div>
                  </div>
                  <div className="filter-control">
                    <label>
                      <span>Price area</span>
                      <span className="filter-value">{priceZones.length ? priceZones.join(', ') : 'Any'}</span>
                    </label>
                    <div className="quick-toggle-row">
                      {['NO1', 'NO2', 'NO3', 'NO4', 'NO5'].map(z => (
                        <button key={z} className={`tech-btn${priceZones.includes(z) ? ' active' : ''}`}
                          onClick={() => setPriceZones(prev => prev.includes(z) ? prev.filter(x => x !== z) : [...prev, z])}>{z}</button>
                      ))}
                    </div>
                  </div>
                  <div className="filter-control">
                    <label>
                      <span>Distance to coastline (cooling intake)</span>
                      <span className="filter-value">{maxCoastKm == null ? 'Any' : `≤ ${maxCoastKm} km`}</span>
                    </label>
                    <div className="quick-toggle-row">
                      <button className={`tech-btn${maxCoastKm === null ? ' active' : ''}`} onClick={() => setMaxCoastKm(null)}>Any</button>
                      {[1, 2, 5].map(v => (
                        <button key={v} className={`tech-btn${maxCoastKm === v ? ' active' : ''}`} onClick={() => setMaxCoastKm(v)}>≤{v} km</button>
                      ))}
                    </div>
                  </div>
                  <div className="filter-hint" style={{ padding: '4px 0' }}>Not yet measurable: distance to schools / hospitals, fjord ice, aquaculture near intake and outfall — no open dataset loaded.</div>
                </div>
              </div>
              )}

              {/* Road Access filter group removed 2026-06-05 — both the
                  Max road distance bucket and the Road category (K+ / F+ /
                  E/R) toggle were eating most candidates because NVDB's
                  category lookup is noisy at the parcel-centroid scale
                  (private driveways closer than the actual municipal
                  road, kategori="P" for any unresolved road link).
                  State (maxRoadDistanceKm / roadPreset) and the backend
                  filter parsing stay — they default to null/'any' so
                  no filter applies, but re-adding the UI is a one-line
                  revert if NVDB coverage improves. */}

              {selectedCountry === 'NO' && (
                <div className="filter-hint" style={{ padding: '6px 2px 0' }}>
                  Always excluded: parcels that overlap a protected area or lie in an NVE flood zone, and parcels whose centre is on NiN type A nature or a salmon river. Sites near a protected area or a quick-clay risk point are flagged on the card instead.
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Advanced Filters ────────────────────────────── */}
        {/* Hidden entirely for non-premium users (free tier = no
            advanced filters) and for data centres, which have none. */}
        {isPremium && useCase !== 'data_center' && (
        <div className="advanced-filters-section">
          <div className="advanced-filters-header">
            <span>{t('siteFinder.advancedFilters')}</span>
            {advancedActiveCount > 0 && (
              <span className="section-badge warning">{advancedActiveCount} {t('siteFinder.active')}</span>
            )}
          </div>

          <div className="advanced-filters-content">
              {/* Species + Roads + Water rights — hydrogen/Norway only.
                  Removed 2026-10-14 because no option changed the result:
                  the landslide & flood group (flood zones are always
                  excluded; landslide was never checked), "No salmon"
                  (salmon rivers are always excluded) and the second strict
                  level of biodiversity and water rights (same check as the
                  first). Build season went too: its months came from
                  latitude bands, not MET data. */}
              {useCase !== 'data_center' && <>
              {/* Species */}
              <div className="filter-group">
                <div className="filter-group-header">
                  <h4>{t('siteFinder.biodiversity')}</h4>
                  <span className="filter-group-source">GBIF</span>
                </div>
                <div className="preset-group preset-2">
                  {[
                    { key: 'any',          labelKey: 'all',         descKey: 'noFilter' },
                    { key: 'low-conflict', labelKey: 'lowConflict', descKey: 'noEndangered' },
                  ].map(({ key, labelKey, descKey }) => (
                    <button key={key} className={`preset-btn ${speciesPreset === key ? 'active' : ''}`} onClick={() => setSpeciesPreset(key)}>
                      <span className="preset-label">{t(`siteFinder.${labelKey}`)}</span>
                      <span className="preset-desc">{t(`siteFinder.${descKey}`)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Road Access */}
              <div className="filter-group">
                <div className="filter-group-header">
                  <h4>{t('siteFinder.roadAccess')}</h4>
                  <span className="filter-group-source">Vegvesen NVDB</span>
                </div>
                <div>
                  <div className="preset-sublabel">{t('siteFinder.roadQuality')}</div>
                  <div className="preset-group preset-2">
                    {[
                      { key: 'any',         labelKey: 'all',           descKey: 'noFilter' },
                      { key: 'kommunevei',  labelKey: 'municipalRoad', descKey: 'municipalOrBetter' },
                      { key: 'fylkesvei',   labelKey: 'countyRoad',    descKey: 'countyOrBetter' },
                      { key: 'national',    labelKey: 'nationalRoad',  descKey: 'nationalOnly' },
                    ].map(({ key, labelKey, descKey }) => (
                      <button key={key} className={`preset-btn ${roadPreset === key ? 'active' : ''}`} onClick={() => setRoadPreset(key)}>
                        <span className="preset-label">{t(`siteFinder.${labelKey}`)}</span>
                        <span className="preset-desc">{t(`siteFinder.${descKey}`)}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginTop: '8px' }}>
                  <div className="preset-sublabel">{t('siteFinder.bridgeCapacity')}</div>
                  <div className="preset-group preset-2">
                    {[
                      { key: 'any',      label: t('siteFinder.all'),   desc: t('siteFinder.notChecked') },
                      { key: 'light',    label: '40 t+',               desc: t('siteFinder.pavedRoads') },
                      { key: 'standard', label: '80 t+',               desc: t('siteFinder.electrolyzerDelivery') },
                      { key: 'heavy',    label: '120 t+',              desc: t('siteFinder.heavyTransport') },
                    ].map(({ key, label, desc }) => (
                      <button key={key} className={`preset-btn ${bridgePreset === key ? 'active' : ''}`} onClick={() => setBridgePreset(key)}>
                        <span className="preset-label">{label}</span>
                        <span className="preset-desc">{desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Water Rights */}
              <div className="filter-group">
                <div className="filter-group-header">
                  <h4>{t('siteFinder.waterRights')}</h4>
                  <span className="filter-group-source">NVE Verneplan for vassdrag</span>
                </div>
                <div className="preset-group preset-2">
                  {[
                    { key: 'any',    labelKey: 'all',             descKey: 'noFilter' },
                    { key: 'basic',  labelKey: 'protectionPlan',  descKey: 'excludeProtectedWaters' },
                  ].map(({ key, labelKey, descKey }) => (
                    <button key={key} className={`preset-btn ${waterRightsPreset === key ? 'active' : ''}`} onClick={() => setWaterRightsPreset(key)}>
                      <span className="preset-label">{t(`siteFinder.${labelKey}`)}</span>
                      <span className="preset-desc">{t(`siteFinder.${descKey}`)}</span>
                    </button>
                  ))}
                </div>
              </div>
              </>}

          </div>
        </div>
        )}

        {/* ── Sort + Limit + Search ────────────────────────── */}
        <div className="sort-limit-row">
          <div className="limit-control">
            <div className="sort-limit-label">{t('siteFinder.results')}</div>
            <select
              value={resultLimit}
              onChange={(e) => {
                const n = parseInt(e.target.value);
                // Free tier: defensively ignore picks > cap. Locked
                // options are disabled in the DOM, but a stray click /
                // keyboard navigation shouldn't slip through.
                if (!isPremium && !FREE_TIER_RESULT_OPTIONS.has(n)) return;
                setResultLimit(n);
              }}
              className="limit-select"
            >
              {[10, 25, 50, 100, 200].map((n) => {
                const locked = !isPremium && !FREE_TIER_RESULT_OPTIONS.has(n);
                return (
                  <option key={n} value={n} disabled={locked}>
                    {n}{locked ? ' 🔒' : ''}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {/* Search mode toggle removed — Zone-first is now the only
            mode for every tier. The other three (Grid-first, Smart
            search, Select station) and their dispatch / results /
            picker UI are still in this file but unreachable; can
            be deleted in a follow-up refactor. */}

        {/* ── Substation picker (Feature 4) ────────────────── */}
        {searchMode === 'substation' && (
          <div className="sub-picker-section">
            <input
              className="muni-search-input"
              type="text"
              placeholder={t('siteScreener.searchPlaceholder')}
              value={subSearch}
              onChange={e => {
                const q = e.target.value;
                setSubSearch(q);
                if (subDebounceRef.current) clearTimeout(subDebounceRef.current);
                subDebounceRef.current = setTimeout(async () => {
                  if (!q.trim()) { setSubResults([]); return; }
                  setSubLoading(true);
                  try {
                    const apiBase = import.meta.env.VITE_API_URL || '';
                    const r = await fetch(`${apiBase}/api/v1/sites/search-substations?q=${encodeURIComponent(q)}&limit=10`);
                    if (r.ok) setSubResults(await r.json());
                  } catch {}
                  setSubLoading(false);
                }, 300);
              }}
            />
            {subLoading && <div style={{ fontSize: 10, color: '#7c829e', padding: '2px 0' }}>{t('siteFinder.loading')}</div>}
            {subResults.length > 0 && (
              <div className="sub-results-dropdown">
                {subResults.map(s => (
                  <div
                    key={s.id}
                    className="sub-result-item"
                    onClick={() => {
                      if (pinnedSubstations.length >= 5) return;
                      if (!pinnedSubstations.find(p => p.id === s.id)) {
                        setPinnedSubstations(prev => [...prev, s]);
                      }
                      setSubSearch('');
                      setSubResults([]);
                    }}
                  >
                    <span className="sub-result-name">{s.name}</span>
                    <span className="sub-result-meta">{s.voltage_kv} kV · {s.municipality || ''}</span>
                  </div>
                ))}
              </div>
            )}
            {pinnedSubstations.length > 0 && (
              <div className="sub-chips">
                {pinnedSubstations.map(s => (
                  <span key={s.id} className="sub-chip">
                    {s.name}
                    <button className="sub-chip-remove" onClick={() => setPinnedSubstations(prev => prev.filter(p => p.id !== s.id))}>✕</button>
                  </span>
                ))}
              </div>
            )}
            <div className="sub-radius-row">
              <span style={{ fontSize: 11, color: '#7c829e' }}>{t('siteFinder.radius')}: {subRadius} km</span>
              <input
                type="range" min="1" max="20" step="1"
                value={subRadius}
                onChange={e => setSubRadius(Number(e.target.value))}
                style={{ flex: 1, marginLeft: 8 }}
              />
            </div>
            <button
              className="search-btn"
              disabled={pinnedSubstations.length === 0 || subSearching}
              style={{ marginTop: 8, width: '100%' }}
              onClick={async () => {
                if (!pinnedSubstations.length) return;
                setSubSearching(true);
                setSubCandidates(null);
                try {
                  const apiBase = import.meta.env.VITE_API_URL || '';
                  const r = await fetch(`${apiBase}/api/v1/sites/find-by-substations`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      substation_ids: pinnedSubstations.map(s => s.id),
                      radius_km: subRadius,
                      min_area_ha: 2.0,
                    }),
                  });
                  if (r.ok) setSubCandidates(await r.json());
                } catch(e) { console.warn('find-by-substations failed:', e); }
                setSubSearching(false);
              }}
            >
              {subSearching ? t('siteFinder.searching') : t('siteFinder.searchButton')}
            </button>
            {subCandidates && (
              <div className="sub-candidates">
                <div className="sub-candidates-header">{subCandidates.total_found} {t('siteFinder.candidatesFound')}</div>
                {subCandidates.candidates.slice(0, 15).map((c, i) => (
                  <div key={c.teig_id || i} className="sub-candidate-item">
                    <div className="sub-cand-id">{c.matrikkelnummer || c.teig_id || `${t('siteFinder.candidate')} ${i+1}`}</div>
                    <div className="sub-cand-meta">{c.area_ha?.toFixed(1)} ha · {c.pinned_substation?.name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {(() => {
          // /sites/search is auth-required. Anonymous visitors who hit
          // the Run button got a bare "HTTP 401" before; route them to
          // Auth0 sign-in instead — free tier = authenticated non-premium.
          //
          // Free tier: the request body is always clamped to
          // viewport ∩ FREE_TIER_BBOX (or the whole Helgeland bbox
          // if the viewport is outside). The button is never disabled
          // for out-of-region pan — search just always returns
          // Helgeland sites, which the result fitBounds animation
          // brings the map back to. The floating banner explains why.
          // Italy (Phase 1 skeleton) has no data pipeline yet — the
          // backend rejects country='IT' with a 422 (see /sites/search
          // country guard). Gate the button here instead so the user
          // sees a clear "coming soon" state rather than a failed
          // search + generic error banner.
          const isItPending = selectedCountry === 'IT';
          const runDisabled = isSearching || isItPending;
          // Three independent states drive label + style:
          //   searching → loading spinner + disabled
          //   error     → red retry CTA (clears error on click, retries)
          //   default   → "Find sites in {region}" / "Sign in to run…"
          // Region label is dynamic — pulls from RegionLockContext so
          // it matches the floating "Free tier: X region only" banner
          // and the panel coord-lookup error. Was hardcoded
          // "Find sites in Vestlandet" until 2026-06-08.
          const lockedRegionLabel = regionLock.label || 'Stavanger';
          const buttonLabel = !isAuthenticated
            ? 'Sign in to run screening'
            : isItPending ? 'Italy — coming soon'
            : isSearching ? '⟳ Searching…'
            : error ? '⚠ Search failed — Retry'
            : !isPremium ? `Find sites in ${lockedRegionLabel}`
            : t('siteFinder.findZones');
          const buttonStateClass = isSearching ? 'searching' : (error ? 'error' : '');
          const handleClick = !isAuthenticated
            ? () => loginWithRedirect()
            : isItPending ? () => {}
            : () => { if (error) setError(null); runSearch(); };
          return (
            <div className="search-btn-wrapper">
              {filtersDirty && !isSearching && (
                <div className="filter-changed-banner">
                  ⚠ Filters changed — re-run search to refresh results
                </div>
              )}
              {/* Capacity-first notice removed 2026-06-01 — capacity is
                  now a sort/rank signal across all substations, not a
                  hard gate on which parcels appear. No special user-
                  facing copy needed; the substation grouping in the
                  results panel speaks for itself. */}
              <button
                onClick={handleClick}
                disabled={runDisabled}
                className={`search-btn ${buttonStateClass}`}
              >
                {buttonLabel}
              </button>

              {/* "or" + direct-coord lookup as a secondary entry point.
                  User feedback 2026-05-27: previously the coord search
                  floated at the bottom of the map; moving it inline
                  here makes both ways of finding a site discoverable
                  in the same place. */}
              {onCoordLookup && (
                <>
                  <div className="search-or-divider">
                    <span className="search-or-line" />
                    <span className="search-or-text">or</span>
                    <span className="search-or-line" />
                  </div>
                  <CoordinateSearchBox onLookup={onCoordLookup} variant="inline" />
                </>
              )}
            </div>
          );
        })()}

        {/* Projects badge */}
        {projectCount > 0 && (
          <a href="#/projects" style={{
            display: 'block', textAlign: 'center', padding: '8px 12px', margin: '8px auto 0',
            width: 'fit-content', borderRadius: 8, fontSize: 12, fontWeight: 700,
            background: 'rgba(34,211,238,0.12)', color: '#22d3ee', textDecoration: 'none',
            border: '1px solid rgba(34,211,238,0.3)',
          }}>
            {projectCount} project{projectCount !== 1 ? 's' : ''} saved
          </a>
        )}

        {/* Free-tier project-cap inline message */}
        {!isPremium && projectCount >= FREE_TIER_PROJECT_CAP && (
          <div className="premium-cap-message" style={{ margin: '8px 12px 0' }}>
            Free tier: {projectCount} of {FREE_TIER_PROJECT_CAP} projects used.{' '}
            <a href={UPGRADE_URL} target="_blank" rel="noopener noreferrer">
              Book a call for unlimited →
            </a>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        {/* Flex container so the three result blocks below
            (progress log, capacity overview, sites list) can be
            reordered without restructuring the JSX. Once sites
            arrive, the sites-list block jumps to the top (order:1)
            and the progress log + capacity overview demote below
            it (order:2/3). While the search is still running
            (no sites yet), the default order:auto leaves them in
            source order so the user sees the activity on top. */}
        <div className={`results-stack${(teigResults && teigResults.length > 0) ? ' results-stack-sites-first' : ''}`}>

        {/* ── SSE Search progress log ──────────────────────── */}
        {progressLog.length > 0 && (
          <div className="search-progress-panel results-stack-progress" ref={progressRef}>
            {progressLog.map((entry, i) => (
              <div key={i} className={`progress-line ${entry.stage === 'complete' ? 'done' : ''}`}>
                <span className="progress-dot">{entry.stage === 'complete' ? '✓' : '·'}</span>
                {entry.label}
              </div>
            ))}
            {isSearching && <div className="progress-line searching">
              <span className="progress-dot">·</span>
              Searching…
            </div>}
          </div>
        )}

        {/* Capacity landscape summary (headline + tier-count grid)
            removed 2026-06-08 per user request. The per-card
            capacity pill on each parcel still surfaces the
            substation context, which is what actually drives a
            site-level decision. The top-of-results banner was
            duplicating the same information at the page scope and
            visually competing with the sites list. */}

        {/* ── Sites (parcel results) ──────────────────────────
            Same legacy result-card list regardless of mode. When
            capacity-first is on, parcels are enriched with
            _capacityContext (rendered as a pill on each card via
            renderTeigCard). */}
        {teigResults !== null && (() => {
          const rejectedByReason = teigHardExcluded.reduce((acc, site) => {
            const key = site.excluded_reason || 'Unknown';
            (acc[key] = acc[key] || []).push(site);
            return acc;
          }, {});
          const toggleReason = (r) => {
            setExpandedReasons(prev => {
              const next = new Set(prev);
              if (next.has(r)) next.delete(r); else next.add(r);
              return next;
            });
          };
          return (
          <div className="results-section results-stack-sites" ref={resultsRef}>
            <div className="results-header">
              <h3>{teigResults.length} {t('siteFinder.suitableSitesFound')}</h3>
              {searchMeta?.served_from === 'result_cache' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                  <span title="The same request was run within the last 24 hours, so that run was replayed from the result cache.">
                    Cached result{searchMeta.cached_at ? ` from ${new Date(searchMeta.cached_at).toLocaleString()}` : ''}
                  </span>
                  <button type="button" disabled={isSearching}
                    onClick={() => findTeiger({ ...lastSearchOptsRef.current, fresh: true })}
                    title="Run this search again against the live sources"
                    style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid rgba(34,211,238,0.4)',
                             background: 'rgba(34,211,238,0.12)', color: '#22d3ee', cursor: isSearching ? 'default' : 'pointer' }}>
                    ↻ Run live
                  </button>
                </div>
              )}
            </div>

            {/* Accepted / Rejected tab toggle */}
            {teigHardExcluded.length > 0 && (
              <div style={{ display: 'flex', gap: 4, padding: '0 8px 8px', borderBottom: '1px solid #334155' }}>
                <button
                  onClick={() => setResultsTab('accepted')}
                  style={{
                    flex: 1, padding: '8px 12px', border: 'none', cursor: 'pointer',
                    background: resultsTab === 'accepted' ? '#0e7490' : 'transparent',
                    color: resultsTab === 'accepted' ? '#fff' : '#94a3b8',
                    fontSize: 12, fontWeight: 700, borderRadius: 6,
                    borderBottom: resultsTab === 'accepted' ? '2px solid #22d3ee' : '2px solid transparent',
                  }}>
                  ✓ Accepted ({teigResults.length})
                </button>
                <button
                  onClick={() => setResultsTab('rejected')}
                  style={{
                    flex: 1, padding: '8px 12px', border: 'none', cursor: 'pointer',
                    background: resultsTab === 'rejected' ? '#7f1d1d' : 'transparent',
                    color: resultsTab === 'rejected' ? '#fff' : '#94a3b8',
                    fontSize: 12, fontWeight: 700, borderRadius: 6,
                    borderBottom: resultsTab === 'rejected' ? '2px solid #ef4444' : '2px solid transparent',
                  }}>
                  ✗ Rejected ({teigHardExcluded.length})
                </button>
              </div>
            )}

            {/* Funnel summary removed 2026-06-08 per user request.
                The same per-reason counts are still available below
                via the Rejected tab + the excluded-list breakdown
                — moving them out of the always-visible header. */}

            {resultsTab === 'rejected' && teigHardExcluded.length > 0 ? (
              /* ── Rejected sites, grouped by reason, expandable + clickable ── */
              <div style={{ padding: '8px' }}>
                <div style={{ fontSize: 10, color: '#7c829e', marginBottom: 8, lineHeight: 1.4 }}>
                  Click a reason to see the sites it blocked. Each site is
                  clickable — open it in the analysis panel to inspect the
                  data just like an accepted site.
                </div>
                {Object.entries(rejectedByReason)
                  .sort((a, b) => b[1].length - a[1].length)
                  .map(([reason, sites]) => {
                    const open = expandedReasons.has(reason);
                    return (
                      <div key={reason} style={{ marginBottom: 6, border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, overflow: 'hidden' }}>
                        <button
                          onClick={() => toggleReason(reason)}
                          style={{
                            width: '100%', padding: '8px 12px', border: 'none',
                            background: open ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.06)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            cursor: 'pointer', color: '#fca5a5', fontSize: 12, fontWeight: 600,
                          }}>
                          <span style={{ textAlign: 'left' }}>{open ? '▼' : '▶'} {reason}</span>
                          <span style={{ fontWeight: 700, minWidth: 24, textAlign: 'right' }}>{sites.length}</span>
                        </button>
                        {open && (
                          <div style={{ padding: '4px 8px 8px', background: 'rgba(15,23,42,0.4)' }}>
                            {sites.map((s, i) => {
                              const label = s.municipality_name || s.municipality || `teig ${s.teig_id}`;
                              const grid = s.nearest_grid_distance_km != null
                                ? `${s.nearest_grid_distance_km.toFixed(1)} km / ${s.nearest_grid_voltage_kv || '?'}kV` : '';
                              const area = s.total_area_ha != null ? `${s.total_area_ha.toFixed(1)} ha` : '';
                              return (
                                <div key={s.teig_id || i}
                                  onClick={() => onTeigSelect && onTeigSelect(s, powerCapacityMw)}
                                  style={{
                                    padding: '6px 8px', margin: '3px 0', borderRadius: 4,
                                    background: 'rgba(30,41,59,0.6)', cursor: 'pointer',
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    fontSize: 11, color: '#cbd5e1',
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(30,41,59,0.9)'}
                                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(30,41,59,0.6)'}>
                                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    <strong>{label}</strong> {area && <span style={{ color: '#94a3b8' }}>· {area}</span>} {grid && <span style={{ color: '#94a3b8' }}>· {grid}</span>}
                                  </div>
                                  <span style={{ fontSize: 10, color: '#22d3ee', flexShrink: 0, marginLeft: 8 }}>Open →</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            ) : teigResults.length === 0 ? (
              <div style={{ padding: '12px 8px' }}>
                <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', marginBottom: 12 }}>
                  {teigFunnel?.hard_excluded > 0
                    ? `${teigFunnel.hard_excluded} ${t('siteFinder.sitesBlockedByHardStop')}`
                    : teigFunnel?.assemblages === 0
                      ? t('siteFinder.noSitesLargeEnough')
                      : t('siteFinder.noSitesMatchedCriteria')}
                </div>
                {/* Compact summary only — full drillable panel rendered
                    below inside the Rejected tab. Left here so the zero-
                    accepted case still surfaces why. */}
                {Object.keys(teigHardExcludedSummary).length > 0 && (
                  <div style={{ padding: '8px 0', marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.92)', marginBottom: 4 }}>
                      {teigHardExcluded.length} analysed sites blocked
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
                      Switch to Rejected tab to inspect each one.
                    </div>
                  </div>
                )}
                {wfsErrors.length > 0 && (
                  <div style={{ padding: '8px 0', marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.92)', marginBottom: 4 }}>
                      Kartverket Matrikkelen WFS unreachable
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.70)', marginBottom: 6 }}>
                      The Norwegian cadastre API is temporarily down
                      ({wfsErrors.length} connection point{wfsErrors.length > 1 ? 's' : ''} timed out).
                      No parcel data could be loaded — this is not a filter problem.
                    </div>
                    <details style={{ marginBottom: 6 }}>
                      <summary style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', cursor: 'pointer' }}>
                        Raw errors
                      </summary>
                      <div style={{ marginTop: 4 }}>
                        {wfsErrors.slice(0, 5).map((e, i) => (
                          <div key={i} style={{ fontSize: 11, color: 'rgba(255,255,255,0.60)', padding: '2px 0', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', wordBreak: 'break-all' }}>{e}</div>
                        ))}
                        {wfsErrors.length > 5 && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.40)' }}>+{wfsErrors.length - 5} more…</div>}
                      </div>
                    </details>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
                      Retry in a few minutes. Capacity data above (substation MW, queue, tiers) is unaffected — it comes from a different upstream and is still accurate.
                    </div>
                  </div>
                )}
                {wfsErrors.length === 0 && (
                  <div style={{ fontSize: 11, color: '#7c829e', textAlign: 'center' }}>
                    {t('siteFinder.tryIncreaseDistance')}
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Option 3 (2026-06-03) — split results by result_tier
                    set on the backend:
                      'industrial' — passes the zone gate (Næringsvirksomhet,
                                     industry-keyword Hensynsone, Energianlegg,
                                     etc.). Primary candidates.
                      'rezoning'   — fails the zone gate BUT AR5 land cover
                                     indicates physically buildable terrain.
                                     Requires kommune dialogue before build.
                    Sites without result_tier (legacy / non-NO paths) sort
                    as 'industrial' so behaviour is unchanged. Each tier
                    gets its own header + CTA framing; ranking within a
                    tier stays the same. */}
                {(() => {
                  const _allResults = parcelsWithCapacity || teigResults || [];
                  // Display order (utils/resultOrder): siting-brief results keep
                  // the backend's brief ranking; other searches keep the
                  // display re-sort (capacity tier, slope band, readiness).
                  // Industrial before rezoning either way. The map hover
                  // numbers sites with the same function.
                  const { industrial: _industrial, rezoning: _rezoning } = displayOrder(_allResults);
                  return (
                    <>
                      {_industrial.length > 0 && (
                        <div className="results-tier-section">
                          {_rezoning.length > 0 && (
                            <div className="results-tier-header" style={{
                              padding: '8px 0 6px',
                              fontSize: 12, fontWeight: 600,
                              color: 'rgba(255,255,255,0.75)',
                              letterSpacing: '0.02em',
                            }}>
                              {_industrial.length} industrial-zoned · ready for evaluation
                            </div>
                          )}
                          <div className="results-list">
                            {_industrial.map((t, idx) => renderTeigCard(t, idx))}
                          </div>
                        </div>
                      )}
                      {_rezoning.length > 0 && (
                        <div className="results-tier-section" style={{ marginTop: 18 }}>
                          <div className="results-tier-header" style={{
                            padding: '8px 0 6px',
                            fontSize: 12, fontWeight: 600,
                            color: 'rgba(255,255,255,0.75)',
                            letterSpacing: '0.02em',
                          }}>
                            {_rezoning.length} rezoning candidate{_rezoning.length === 1 ? '' : 's'} · kommune dialogue required
                          </div>
                          {/* Explainer paragraph removed 2026-06-08 per
                              user request — the section header already
                              signals "kommune dialogue required"; the
                              full explanation lives in per-site analysis
                              now. */}
                          <div className="results-list">
                            {_rezoning.map((t, idx) => renderTeigCard(t, _industrial.length + idx))}
                          </div>
                        </div>
                      )}
                      {_industrial.length === 0 && _rezoning.length === 0 && (
                        <div className="results-list" />
                      )}
                    </>
                  );
                })()}
              </>
            )}

            {teigExcluded.length > 0 && (() => {
              // The SSE payload caps the per-item excluded list at 80
              // for transport size, but the funnel has the REAL counts
              // per reason (heritage / dispute / too_small / substation).
              // Sum those to get the true total so the header doesn't
              // misrepresent 80 as the universe when the real number is
              // 1600+ slivers. (Same false-precision problem as the
              // placebo filters we cut earlier — info that looks
              // complete but isn't.)
              const f = teigFunnel || {};
              const realTotal =
                (f.kulturminne || 0)
                + (f.tvist || 0)
                + (f.too_small || 0)
                + (f.substation || 0);
              // Funnel might not be populated yet (e.g. legacy SSE
              // response). Fall back to the list length, with a "+"
              // marker so the user still sees a hint of truncation.
              const headerCount = realTotal > 0 ? realTotal : teigExcluded.length;
              const truncated = realTotal > teigExcluded.length;
              return (
                <div className="excluded-section">
                  <div className="excluded-header" onClick={() => setTeigExcludedExpanded(e => !e)}>
                    <span>
                      {headerCount} site{headerCount === 1 ? '' : 's'} excluded
                      {truncated && (
                        <span style={{ marginLeft: 6, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
                          (showing first {teigExcluded.length})
                        </span>
                      )}
                    </span>
                    <span>{teigExcludedExpanded ? 'hide' : 'show'}</span>
                  </div>
                  {teigExcludedExpanded && (
                    <>
                      {truncated && (
                        <div style={{
                          padding: '6px 10px', fontSize: 11,
                          color: 'rgba(255,255,255,0.55)',
                          background: 'rgba(255,255,255,0.02)',
                          borderRadius: 4, marginBottom: 6,
                        }}>
                          Breakdown by reason (full count):
                          {[
                            f.kulturminne && `${f.kulturminne} heritage`,
                            f.tvist && `${f.tvist} dispute`,
                            f.too_small && `${f.too_small} too small / sliver / zone gate`,
                            f.substation && `${f.substation} on-substation polygon`,
                          ].filter(Boolean).map((s, i) => (
                            <span key={i} style={{ marginLeft: 6 }}>· {s}</span>
                          ))}
                        </div>
                      )}
                      <div className="excluded-list">
                        {teigExcluded.map((t, i) => {
                          // Clickable when we have coords. Sliver / heritage
                          // rejects still ran through the pre-filter so they
                          // carry lat / lon / geometry / matrikkelnummer.
                          // Sites without coords (rare — only when the parcel
                          // shape failed to load) stay inert.
                          const clickable = onTeigSelect && t.lat != null && t.lon != null;
                          return (
                            <div key={i} className="excluded-item"
                                 onClick={clickable
                                   ? () => onTeigSelect(t, powerCapacityMw)
                                   : undefined}
                                 style={clickable
                                   ? { cursor: 'pointer' }
                                   : undefined}>
                              <span className="excluded-name">{t.matrikkelnummer || t.teig_id || `Parcel ${i+1}`}</span>
                              <span className="excluded-reason">{t.excluded_reason}</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
          );
        })()}

        </div>{/* /.results-stack */}

        {/* ── Zone-first results ───────────────────────────── */}
        {zoneResults && (() => {
          const pinnedZones = selectedMunicipalities.length > 0
            ? PINNED_SITES_DATA.sites
                .filter(s => selectedMunicipalities.includes(s.municipality_code))
                .map(s => ({
                  zone_id: `pinned_${s.id}`,
                  zone_name: s.name,
                  zone_code: 'I1500',
                  zone_suitability: s.verdict === 'final_candidate' ? 0.95 : s.verdict === 'shortlisted' ? 0.75 : 0.5,
                  area_ha: parseFloat(s.area_ha) || null,
                  municipality: s.municipality_code,
                  location: { longitude: s.lon, latitude: s.lat },
                  nearest_grid: { id: null, name: 'Nedre Røssåga', voltage_level: 420, distance_km: parseFloat(s.transmission_km) || null },
                  _pinned: s,
                }))
            : [];
          const allZones = [...pinnedZones, ...zoneResults.zones];
          return (
          <div className="results-section" ref={resultsRef}>
            <div className="results-header">
              <h3>{zoneResults.total} {t('siteFinder.gridConnectionsFound')}</h3>
              <span className="results-count">{t('siteFinder.zoomToSeeParcels')}</span>
            </div>
            <div className="results-list">
              {allZones.map((z) => {
                const muniName = KOMMUNE_NAMES[z.municipality] || z.municipality;
                const distKm = z.nearest_grid?.distance_km;
                const capex = Math.round(4 * (distKm || 0));
                const { zone: priceZone, price_nok_mwh } = getPriceZone(z.municipality);
                const priceOre = (price_nok_mwh / 10).toFixed(0);
                const zoneSuit = z.zone_suitability;
                const zBadgeClass = zoneSuit >= 0.7 ? 'rc-zone-green'
                  : zoneSuit >= 0.4 ? 'rc-zone-amber'
                  : zoneSuit != null ? 'rc-zone-red' : 'rc-zone-grey';
                const gridKv = z.nearest_grid?.voltage_level;
                return (
                <div key={z.zone_id} className="result-card"
                  onClick={() => onSubstationPan && onSubstationPan(zoneToSite(z))}>
                  {/* Top row: zone badge + municipality + area */}
                  <div className="rc-top-row">
                    {(() => { const oz = overrideZone(z.zone_code, z.zone_name); return (
                    <span className={`rc-zone-badge ${zBadgeClass}`} title={oz.name || ''}>
                      {oz.code ? (oz.name ? `${oz.code} ${oz.name}` : String(oz.code)) : 'Unknown zone'}
                    </span>); })()}
                    <span className="rc-municipality">{muniName}</span>
                    {z.area_ha != null && <span className="rc-area-badge">{Number(z.area_ha).toFixed(1)} ha</span>}
                  </div>

                  {/* Grid info */}
                  <div className="rc-grid-row">
                    {[z.nearest_grid?.name, gridKv ? `${gridKv} kV` : null, distKm != null ? `${Number(distKm).toFixed(1)} km` : null].filter(Boolean).join(' · ')}
                  </div>

                  {/* Zoom hint */}
                  <div className="rc-flags-row">
                    <span style={{ fontSize: 10, color: '#7c829e', fontStyle: 'italic' }}>Zoom in on the map to see available parcels</span>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
          );
        })()}

        {/* ── Smart search results ──────────────────────────── */}
        {smartResults && smartResults.clusters && (
          <div className="results-section" ref={resultsRef}>
            <div className="results-header">
              <h3>{smartResults.clusters_found} site zones found</h3>
              <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0' }}>
                {smartResults.grid_stats.total_cells.toLocaleString()} cells evaluated ·{' '}
                {smartResults.grid_stats.pct_excluded}% excluded ·{' '}
                {smartResults.elapsed_s}s
              </p>
            </div>
            <div className="results-list" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
              {smartResults.clusters.map((c, i) => (
                <div key={c.cluster_id}
                  className="teig-list-item"
                  style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }}
                  onClick={async () => {
                    // 1. Fly to cluster centroid
                    if (onSubstationPan) onSubstationPan(c.centroid_lon, c.centroid_lat, 14);

                    // 2. Build a teig-like object from the cluster data
                    //    so the existing analysis panel can render it
                    const syntheticTeig = {
                      teig_id: `grid-cluster-${c.cluster_id}`,
                      matrikkelnummer: `Grid zone #${i + 1}`,
                      lat: c.centroid_lat,
                      lon: c.centroid_lon,
                      areal_ha: c.area_ha,
                      total_area_ha: c.area_ha,
                      constituent_count: c.cell_count,
                      kommunenummer: null,
                      zone_code: 'grid_scan',
                      zone_name: `${c.area_ha.toFixed(0)} ha viable zone`,
                      zone_suitability: c.avg_score,
                      nearest_grid_id: null,
                      nearest_grid_name: c.primary_substation,
                      nearest_grid_voltage_kv: c.max_voltage_kv,
                      nearest_grid_distance_km: c.min_grid_km,
                      nearest_grid_price_zone: null,
                      dual_feed_available: false,
                      fi_broadband_1gbit: c.has_broadband_1g,
                      fi_broadband_100mbit: !c.has_broadband_1g && c.avg_broadband > 0,
                      fi_broadband_data_available: true,
                      fi_broadband_note: c.has_broadband_1g
                        ? '1 Gbit/s available (fiber confirmed)'
                        : 'No fixed broadband at this zone',
                      nearest_ixp_name: null,
                      nearest_ixp_distance_km: null,
                      country: 'FI',
                      _from_search: true,
                      _grid_cluster: true,
                    };

                    // 3. Pass to the existing teig select handler
                    if (onTeigSelect) {
                      onTeigSelect(syntheticTeig, powerCapacityMw);
                    }
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>
                      #{i + 1} · {c.area_ha.toFixed(0)} ha
                    </span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '2px 6px', borderRadius: 6,
                      background: c.avg_score >= 0.7 ? '#22c55e22' : c.avg_score >= 0.5 ? '#fbbf2422' : '#ef444422',
                      color: c.avg_score >= 0.7 ? '#22c55e' : c.avg_score >= 0.5 ? '#fbbf24' : '#ef4444',
                    }}>
                      {(c.avg_score * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                    {c.primary_substation} · {c.max_voltage_kv} kV · {c.min_grid_km} km
                    {c.has_broadband_1g && <span style={{ color: '#22c55e', marginLeft: 6 }}>1G fiber</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Grid-first results ───────────────────────────── */}
        {results && (
          <div className="results-section" ref={resultsRef}>
            <div className="results-header">
              <h3>{results.sites.length} {t('siteFinder.gridConnectionsFound')}</h3>
              <span className="results-count">{t('siteFinder.zoomToSeeParcels')}</span>
            </div>

            {/* Sort toggle */}
            <div className="sort-toggle">
              <span>Sort:</span>
              {[
                ['voltage',   'Voltage'],
                ['distance',  'Grid dist'],
                ...(useCase !== 'data_center' ? [['port', 'Port dist']] : []),
                ['water',     'Water dist'],
                ['protected', 'Protected'],
              ].map(([mode, label]) => (
                <button key={mode}
                  className={`sort-btn ${sortMode === mode ? 'active' : ''}`}
                  onClick={() => setSortMode(mode)}
                >{label}</button>
              ))}
            </div>

            <div className="results-list">
              {sortedSites.map((site, idx) => {
                const { zone: priceZone, price_nok_mwh } = getPriceZone(site.municipality_number);
                const priceOre = (price_nok_mwh / 10).toFixed(0);
                const muniName = site.municipality_name || site.zoning_info?.municipality || site.municipality_number;
                const zoneSuit = site.zone_suitability;
                const sBadgeClass = zoneSuit >= 0.7 ? 'rc-zone-green'
                  : zoneSuit >= 0.4 ? 'rc-zone-amber'
                  : zoneSuit != null ? 'rc-zone-red' : 'rc-zone-grey';
                const gridCapexEst = getGridCapexEstimate(site);
                const hasAnyBlocker = site.flood_hard_stop || site.landslide_hard_stop
                  || site.has_automatically_protected_kulturminner || site.has_salmon || site.is_salmon_river
                  || site.nin_type_a_conflict || site.in_protected_watershed;
                return (
                  <div
                    key={idx}
                    className="result-card"
                    onClick={() => onSubstationPan && onSubstationPan(site)}
                  >
                    {/* Top row: zone badge + municipality + area */}
                    <div className="rc-top-row">
                      {(() => { const oz = overrideZone(site.zone_code, site.zoning_info?.zone_name); return (
                      <span className={`rc-zone-badge ${sBadgeClass}`} title={oz.name || ''}>
                        {oz.code ? (oz.name ? `${oz.code} ${oz.name}` : String(oz.code)) : 'Unknown zone'}
                      </span>); })()}
                      <span className="rc-municipality">{muniName}</span>
                      {site.total_parcel_area_ha != null && <span className="rc-area-badge">{Number(site.total_parcel_area_ha).toFixed(1)} ha</span>}
                    </div>

                    {/* Grid info */}
                    <div className="rc-grid-row">
                      {[site.nearest_grid?.name, site.voltage_level_kv != null ? `${site.voltage_level_kv} kV` : null, site.grid_distance_km != null ? `${site.grid_distance_km} km` : null].filter(Boolean).join(' · ')}
                    </div>

                    {/* Port · Species · Site info */}
                    <div className="rc-facts-row">
                      {(site.nearest_port || site.port_distance_km != null) && (
                        <span className="rc-fact-item">
                          
                          {site.nearest_port ? site.nearest_port + (site.port_distance_km != null ? ` · ${Number(site.port_distance_km).toFixed(0)} km` : '') : `Port ${Number(site.port_distance_km).toFixed(0)} km`}
                        </span>
                      )}
                      {(site.red_listed_species_count != null || site.has_red_listed_species != null) && (
                        <span className={`rc-fact-item ${(site.red_listed_species_count ?? 0) > 0 ? 'rc-fact-warn' : 'rc-fact-ok'}`}>
                          
                          {(site.red_listed_species_count ?? 0) > 0 ? `${site.red_listed_species_count} red-listed sp.` : 'No red-listed'}
                        </span>
                      )}
                      {(site.slope_pct != null || site.buildable_contiguous_ha != null || site.urban_distance_km != null) && (
                        <span className="rc-fact-item">
                          {[
                            site.buildable_contiguous_ha != null && site.buildable_contiguous_ha > 0
                              ? `${site.buildable_contiguous_ha.toFixed(1)} ha flat platform`
                              : site.slope_pct != null ? `${site.slope_pct.toFixed(0)}% slope` : null,
                            site.urban_distance_km != null ? `${site.urban_distance_km.toFixed(1)} km settlement` : null,
                          ].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </div>

                    {/* Flag chips. Audit 2026-05-27: real concerns
                        (slope > 35%, unknown IXP, etc.) now override
                        the "No blockers" pill so users don't see
                        green-light status on a 100% slope cliff. */}
                    <div className="rc-flags-row">
                      {site.flood_hard_stop && <span className="rc-flag-chip rc-flag-bad">Flood zone</span>}
                      {site.kvikkleire_hard_stop && <span className="rc-flag-chip rc-flag-warn">Quick clay (caution)</span>}
                      {site.landslide_hard_stop && <span className="rc-flag-chip rc-flag-bad">Landslide</span>}
                      {site.has_automatically_protected_kulturminner && <span className="rc-flag-chip rc-flag-warn">Heritage</span>}
                      {(site.has_salmon || site.is_salmon_river) && <span className="rc-flag-chip rc-flag-bad">Salmon river</span>}
                      {site.nin_type_a_conflict && <span className="rc-flag-chip rc-flag-bad">NiN A</span>}
                      {site.in_protected_watershed && <span className="rc-flag-chip rc-flag-bad">Protection plan</span>}
                      {/* Long-text concerns (Protected area X km, Flood
                          caution map, …) dropped 2026-06-08 per user
                          request — only structured-label types
                          (slope / ixp) render as chips. */}
                      {(site.concerns || []).map((c, i) => {
                        let label = null;
                        if (c.type === 'slope_unbuildable') label = `⚠ ${c.message.split(' — ')[0]} cliff`;
                        else if (c.type === 'slope_steep')     label = c.message.split(' — ')[0];
                        else if (c.type === 'ixp_unknown')     label = 'IXP unknown';
                        else if (c.type === 'ixp_far')         label = `IXP ${c.message.match(/(\d+)\s*km/)?.[1] || '?'} km`;
                        if (!label) return null;
                        return (
                          <span key={`concern-${i}`}
                                className={`rc-flag-chip ${c.severity === 'high' ? 'rc-flag-bad' : 'rc-flag-warn'}`}
                                title={c.message}>
                            {label}
                          </span>
                        );
                      })}
                      {!hasAnyBlocker && (!site.concerns || site.concerns.length === 0) && (
                        <span className="rc-flag-chip rc-flag-clear">✓ No blockers</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Excluded sites — collapsed by default */}
            {excludedSites?.length > 0 && (
              <div className="excluded-section">
                <div className="excluded-header" onClick={() => setExcludedExpanded(e => !e)}>
                  <span>{excludedSites.length} sites excluded</span>
                  <span>{excludedExpanded ? 'hide' : 'show'}</span>
                </div>
                {excludedExpanded && (
                  <div className="excluded-list">
                    {excludedSites.map((site, i) => (
                      <div key={i} className="excluded-item">
                        <span className="excluded-name">{site.name || site.nearest_grid?.name || `Sted ${i+1}`}</span>
                        <span className="excluded-reason">{site.excluded_reason}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        </div>
        </>
      )}

      {/* Cursor tooltip for search status */}
      {cursorTooltip.visible && (
        <div 
          className={`cursor-search-tooltip ${cursorTooltip.fading ? 'fading' : ''}`}
          style={{ left: mousePos.x + 15, top: mousePos.y + 15 }}
        >
          {cursorTooltip.text}
        </div>
      )}
    </div>
  );
};

export default SiteFinder;
